/**
 * Types partagés front / back — ARCHITECTURE.md §3.
 *
 * Consommés par chemin relatif depuis `server/src` et `web/src` : ce dossier
 * n'est pas un paquet npm interne et ne doit jamais le devenir (§1.1, pas
 * d'outillage de monorepo).
 *
 * Règle : ce fichier ne contient que du vocabulaire commun à l'API et au
 * client (énumérations, DTO). Aucune logique de calcul — les macros sont
 * toujours recalculées côté serveur (§5.1).
 *
 * ⚠️ Une seule exception, ouverte à l'étape 9 : `roundMacroForDisplay`. C'est
 * une règle de *présentation*, pas un calcul, et elle a bien deux
 * consommateurs — l'écran Aujourd'hui et le texte du bilan de fin de journée,
 * composé côté serveur. Voir son commentaire. Ne pas élargir cette brèche à des
 * fonctions qui *produisent* des macros.
 */

/* --------------------------------------------------------------------------
 * Énumérations — miroir des colonnes `text({ enum })` de server/src/db/schema.ts
 * ----------------------------------------------------------------------- */

/** Origine d'un aliment (§4.3). La base perso prime toujours (specs §7.2). */
export type FoodSource = 'perso' | 'openfoodfacts';

/** Unité de référence d'un aliment : les macros sont données pour 100 g ou 100 ml. */
export type BaseUnit = 'g' | 'ml';

/** Expression du rendement d'une recette (§4.3). */
export type RecipeYieldType = 'portions' | 'weight';

/** Nature d'une entrée de journal (§4.5). `quick` = saisie libre (specs §6.3d). */
export type LogEntryType = 'food' | 'recipe' | 'quick';

/** Les trois notifications de la V1 (specs §6.7, archi §4.7). */
export type NotificationType = 'weigh_in' | 'empty_log' | 'daily_summary';

/* --------------------------------------------------------------------------
 * Macros
 * ----------------------------------------------------------------------- */

/**
 * Jeu de macronutriments. Les quatre valeurs sont indépendantes : `kcal` n'est
 * jamais dérivé de P/G/L, ni en objectif (§4.4) ni en consommé (§4.5).
 *
 * Fibres et micronutriments sont hors périmètre à toutes les versions
 * (specs §7.5) : ne jamais ajouter de champ ici.
 */
export interface MacroSet {
  /** Protéines, en grammes. */
  protein: number;
  /** Glucides, en grammes. */
  carbs: number;
  /** Lipides, en grammes. */
  fat: number;
  /** Énergie, en kilocalories. */
  kcal: number;
}

/** Coefficients Atwater utilisés pour l'écart kcal *indicatif* (§5.3). */
export const KCAL_PER_GRAM: Readonly<Record<'protein' | 'carbs' | 'fat', number>> =
  Object.freeze({ protein: 4, carbs: 4, fat: 9 });

/**
 * Arrondi **d'affichage** : la kcal entière, le dixième de gramme.
 *
 * ⚠️ Ce n'est pas un calcul de macros — le serveur en reste seul juge (§5.1) —
 * mais une règle de présentation, et elle est ici parce qu'elle a **deux**
 * consommateurs depuis l'étape 9 : l'écran Aujourd'hui, qui la rend en React,
 * et le bilan de fin de journée (specs §6.7), dont le texte est composé côté
 * serveur au moment de pousser la notification. Les specs demandent que le
 * bilan porte « l'état des macros » : s'il affichait 120 g là où l'écran
 * affiche 120,4, c'est le bilan qu'on cesserait de croire.
 *
 * Une troisième duplication assumée — après `lib/foodPreview.ts` et
 * `lib/recipePreview.ts` — aurait été le mauvais marché ici : celles-là
 * existent pour la mise à jour optimiste, alors que celle-ci n'aurait servi
 * qu'à écrire deux fois `Math.round`.
 */
export function roundMacroForDisplay(value: number, isEnergy: boolean): number {
  return isEnergy ? Math.round(value) : Math.round(value * 10) / 10;
}

/* --------------------------------------------------------------------------
 * DTO d'API
 * ----------------------------------------------------------------------- */

/**
 * Réponse de `GET /api/health` — route publique (§5.1), utilisée comme
 * garde-fou de fin de déploiement (§7.6).
 */
export interface HealthResponse {
  status: 'ok';
  /** Épreuve de vie de SQLite : la base est ouverte et répond. */
  db: 'ok';
  /** Secondes écoulées depuis le démarrage du process. */
  uptimeSeconds: number;
  /** Horodatage serveur, epoch millisecondes UTC. */
  now: number;
}

/* --------------------------------------------------------------------------
 * Authentification (§4.2, §5.1) — étape 4
 * ----------------------------------------------------------------------- */

/** Corps d'erreur uniforme de l'API. Toute réponse ≥ 400 a cette forme. */
export interface ApiErrorResponse {
  error: {
    /** Code stable, destiné au code client. */
    code: string;
    /** Message en français, destiné à l'affichage. */
    message: string;
  };
}

/**
 * Réponse de `GET /api/auth/status` — publique, c'est elle qui décide de
 * l'écran affiché au chargement de la SPA.
 */
export interface AuthStatusResponse {
  /** Une session valide accompagne la requête. */
  authenticated: boolean;
  /**
   * Aucun compte n'existe encore : la première passkey reste à enregistrer.
   * Bascule définitivement à `false` une fois l'amorçage effectué.
   */
  bootstrapRequired: boolean;
  /** `RP_ID` effectif du serveur — utile pour diagnostiquer un désaccord de domaine. */
  rpId: string;
}

/** Passkey enregistrée, telle que listée dans les Réglages. */
export interface PasskeySummary {
  id: number;
  /** Étiquette libre saisie à l'enregistrement (« iPhone », « portable »). */
  deviceLabel: string | null;
  transports: string[];
  createdAt: number;
  lastUsedAt: number | null;
}

/** Réponse de `GET /api/auth/me` — nécessite une session. */
export interface AccountResponse {
  createdAt: number;
  /** Date de la clé de secours en vigueur, `null` si aucune. */
  recoveryCreatedAt: number | null;
  passkeys: PasskeySummary[];
}

/**
 * Clé de secours en clair. ⚠️ Renvoyée **une seule fois**, à sa création : ni
 * l'API ni la base ne permettent de la relire ensuite (§9).
 */
export interface RecoveryKeyResponse {
  recoveryKey: string;
}

/** Réponse de `POST /api/auth/bootstrap/verify` : compte créé, session ouverte. */
export interface BootstrapResponse extends RecoveryKeyResponse {
  passkey: PasskeySummary;
}

/* --------------------------------------------------------------------------
 * Idempotence (règle non négociable n°9, archi §11) — étape 5
 * ----------------------------------------------------------------------- */

/**
 * Identifiant tiré par le client, transporté par toute mutation de *création*
 * du journal. Rejouer la même création avec le même `clientId` renvoie la ligne
 * déjà écrite au lieu d'en créer une seconde — c'est ce qui rendra sûre la file
 * de synchronisation hors ligne de la V2 (§11).
 *
 * Les autres verbes n'en ont pas besoin : un `PUT` / `PATCH` porte l'identifiant
 * de la ressource, et un `DELETE` rejoué laisse le serveur dans le même état
 * (RFC 9110 §9.2.2 — l'idempotence porte sur l'état, pas sur le code de retour).
 */
export interface ClientIdentified {
  /** Chaîne opaque, unique côté client (un UUID fait très bien l'affaire). */
  clientId?: string;
}

/* --------------------------------------------------------------------------
 * Écart kcal (specs §6.2, archi §5.3) — étape 5
 * ----------------------------------------------------------------------- */

/**
 * Écart entre les kcal **saisies** et le total théorique 4/4/9 reconstruit
 * depuis P/G/L.
 *
 * ⚠️ Strictement **indicatif** : aucun endpoint ne corrige, ne bloque, ni ne
 * suggère de correction (specs §6.2, archi §5.3). C'est un révélateur d'une
 * incohérence dans les objectifs que l'utilisateur a lui-même saisis, rien de
 * plus. Les quatre cibles restent indépendantes (§4.4).
 */
export interface KcalGap {
  /** `kcal` tel que saisi. */
  entered: number;
  /** `4 × protéines + 4 × glucides + 9 × lipides`. */
  theoretical: number;
  /** `entered − theoretical`. Négatif = les kcal saisies sont en dessous. */
  delta: number;
}

/* --------------------------------------------------------------------------
 * Modèles de journée et planning (§4.4, specs §6.1) — étape 5
 * ----------------------------------------------------------------------- */

/** Repas type d'un modèle, avec ses cibles propres (specs §6.1). */
export interface TemplateMealDTO {
  id: number;
  name: string;
  position: number;
  targets: MacroSet;
  kcalGap: KcalGap;
}

/**
 * Modèle de journée (§4.4).
 *
 * ⚠️ `targets` est **calculé** : c'est la somme des cibles des repas types, pas
 * une saisie. Voir `DayTemplateInput`, qui ne l'accepte donc pas en écriture.
 */
export interface DayTemplateDTO {
  id: number;
  name: string;
  /** Somme des `targets` de `meals`, calculée par le serveur. Lecture seule. */
  targets: MacroSet;
  kcalGap: KcalGap;
  meals: TemplateMealDTO[];
  createdAt: number;
}

/** Repas type en écriture — pas d'`id` : la liste est remplacée en bloc. */
export interface TemplateMealInput {
  name: string;
  targets: MacroSet;
}

/**
 * Corps de `POST /api/templates` et `PUT /api/templates/:id`.
 *
 * ⚠️ **Pas de `targets` de journée** : le total du modèle est la somme des
 * repas, et c'est le serveur qui la fait. Saisir les deux séparément laissait
 * dériver un total qui ne correspondait à aucune addition de repas — l'écart
 * était visible mais rien ne le résolvait. Les quatre macros restent en revanche
 * indépendantes **entre elles** (§4.4) : `kcal` n'est jamais déduit de P/G/L, ni
 * pour un repas, ni pour le total.
 */
export interface DayTemplateInput extends ClientIdentified {
  name: string;
  meals: TemplateMealInput[];
}

/** Surcharge ponctuelle : `date → modèle`. **Prime toujours** sur la récurrence. */
export interface PlanningOverrideDTO {
  /** `YYYY-MM-DD`. */
  date: string;
  templateId: number;
}

/**
 * Planning complet (§4.4).
 *
 * `weekly` a **toujours** sept cases, indexées comme la colonne `weekday` :
 * 0 = lundi … 6 = dimanche. `null` = aucune récurrence ce jour-là.
 */
export interface PlanningResponse {
  weekly: (number | null)[];
  overrides: PlanningOverrideDTO[];
}

/* --------------------------------------------------------------------------
 * Journal (§4.5, specs §6.2 et §6.3) — étape 5
 * ----------------------------------------------------------------------- */

/**
 * Entrée de journal telle que lue.
 *
 * ⚠️ `label`, `portionLabel` et `macros` sont les valeurs **figées** à
 * l'enregistrement (§4.1, specs §7.1). `foodId` / `recipeId` sont informatifs :
 * aucun affichage ne doit repartir d'eux pour reconstituer un libellé ou des
 * macros.
 */
export interface LogEntryDTO {
  id: number;
  type: LogEntryType;
  foodId: number | null;
  recipeId: number | null;
  label: string;
  quantity: number;
  unit: string;
  portionLabel: string | null;
  macros: MacroSet;
  /**
   * Bloc groupé d'une entrée de type `recipe` (specs §6.5) — **vide pour tout
   * autre type**.
   *
   * ⚠️ Ces lignes sont elles aussi **figées** (§4.5) : ce sont les quantités
   * ajustées au moment de l'ajout, jamais les ingrédients de la recette lus à
   * l'affichage. Une recette modifiée ou supprimée ne les touche pas.
   */
  ingredients: LogEntryIngredientDTO[];
  position: number;
  createdAt: number;
  /**
   * Identifiant tiré par le client à la création, ou `null` pour une ligne née
   * avant que la règle n°9 n'existe (ou créée sans `clientId`).
   *
   * 🔴 **Exposé pour que la file de synchronisation puisse résoudre un
   * place-titre** (`ARCHITECTURE_V2.md` §2.3-a, étape 3). Toutes les mutations
   * de journal renvoient la **journée complète**, jamais la ligne créée : sans
   * ce champ, retrouver le repas qu'on vient d'écrire pour en donner l'`id` à
   * l'opération suivante serait une devinette — « le dernier de la liste », « le
   * plus grand `id` » —, et une devinette qui se trompe écrit une entrée dans le
   * mauvais repas. Ici la file relit **l'identité qu'elle a elle-même choisie**.
   *
   * ⚠️ Ce n'est pas une donnée à afficher : aucun écran ne le lit.
   */
  clientId: string | null;
}

/**
 * Ligne d'ingrédient d'un bloc recette journalisé (§4.5, `log_entry_ingredient`).
 *
 * ⚠️ `label` et `macros` sont **copiés** à l'écriture, comme pour l'entrée
 * parente. `foodId` est informatif et en `SET NULL` : il ne sert qu'à la
 * ré-édition, jamais à reconstituer ce qui est affiché.
 *
 * `quantity` est **toujours** exprimée dans l'unité de base de l'aliment : le
 * bloc journalisé ne connaît pas les portions nommées, que l'éditeur de recette
 * a déjà converties.
 */
export interface LogEntryIngredientDTO {
  id: number;
  foodId: number | null;
  label: string;
  quantity: number;
  /** Unité de base de l'aliment au moment de l'ajout (`g` ou `ml`). */
  unit: string;
  macros: MacroSet;
  position: number;
}

/** Repas d'une journée — ajoutable, renommable, supprimable librement (§4.5). */
export interface MealDTO {
  id: number;
  name: string;
  position: number;
  /** Cibles *snapshotées* depuis le repas type, ou saisies à la main. */
  targets: MacroSet;
  /** Somme des entrées du repas, calculée par le serveur. */
  consumed: MacroSet;
  /** `targets − consumed`. Peut être négatif : un dépassement se voit. */
  remaining: MacroSet;
  /**
   * Repas marqué comme **réutilisable** : il est alors proposé à la recopie
   * depuis l'écran d'ajout (`SavedMealDTO`). Se règle depuis le journal, seul
   * endroit où l'on voit ce que le repas contient.
   */
  isFavorite: boolean;
  entries: LogEntryDTO[];
  /**
   * Identifiant tiré par le client à la création, ou `null` pour une ligne née
   * avant que la règle n°9 n'existe (ou créée sans `clientId`).
   *
   * 🔴 **Exposé pour que la file de synchronisation puisse résoudre un
   * place-titre** (`ARCHITECTURE_V2.md` §2.3-a, étape 3). Toutes les mutations
   * de journal renvoient la **journée complète**, jamais la ligne créée : sans
   * ce champ, retrouver le repas qu'on vient d'écrire pour en donner l'`id` à
   * l'opération suivante serait une devinette — « le dernier de la liste », « le
   * plus grand `id` » —, et une devinette qui se trompe écrit une entrée dans le
   * mauvais repas. Ici la file relit **l'identité qu'elle a elle-même choisie**.
   *
   * ⚠️ Ce n'est pas une donnée à afficher : aucun écran ne le lit.
   */
  clientId: string | null;
}

/**
 * Journée complète — la charge utile de l'écran « Aujourd'hui ».
 *
 * C'est aussi la réponse de **toutes** les mutations de journal : le client
 * remplace son cache d'un bloc, sans second aller-retour. C'est ce qui permet
 * de réconcilier la mise à jour optimiste du bandeau (§6.2) en une seule fois.
 */
export interface DayDTO {
  id: number;
  /** `YYYY-MM-DD`. */
  date: string;
  /** Informatif : la journée est indépendante du modèle dès sa création (§7.4). */
  sourceTemplateId: number | null;
  /**
   * Somme des `targets` de `meals`, calculée par le serveur à chaque lecture.
   * **Lecture seule** — exactement comme `DayTemplateDTO.targets`.
   *
   * 🔴 **Plus un snapshot depuis le 04/09/2026** (migration `0013`, demandé
   * après usage). Ce sont les **repas** qui portent le snapshot du modèle ;
   * leur somme est le total du jour, donc corriger la cible d'un repas met à
   * jour celle de la journée sans que rien n'ait à être recalculé quelque part.
   */
  targets: MacroSet;
  consumed: MacroSet;
  /** `targets − consumed` : c'est l'information la plus consultée (specs §6.2). */
  remaining: MacroSet;
  kcalGap: KcalGap;
  /**
   * Aucun modèle n'a pu être résolu à la création : cibles à zéro et aucun
   * repas. Cas du tout premier lancement, avant qu'un modèle n'existe.
   * L'écran invite alors à appliquer un modèle (`POST …/apply-template`).
   */
  needsTemplate: boolean;
  meals: MealDTO[];
  createdAt: number;
}

/**
 * Saisie libre (specs §6.3-d) — **seule voie fonctionnelle à l'étape 5**.
 *
 * Les quatre macros sont saisies directement et valent pour l'entrée entière :
 * `quantity` / `unit` ne sont ici que descriptifs (« 1 assiette »), jamais des
 * multiplicateurs. Aucune fiche n'est créée en base, conformément au §6.3-d.
 */
export interface QuickEntryInput {
  type: 'quick';
  label: string;
  /** Défaut : 1. */
  quantity?: number;
  /** Défaut : « portion ». */
  unit?: string;
  macros: MacroSet;
}

/**
 * Ajout d'un aliment de la base personnelle (specs §6.3-b et §6.4) — étape 6.
 *
 * ⚠️ **Aucune macro n'est transmise.** Le client désigne l'aliment et la
 * quantité ; le serveur lit `food.protein_100` & co. **une seule fois**, à
 * l'écriture, et fige le résultat dans `log_entry` (§4.1, §5.1). Corriger ou
 * supprimer l'aliment ensuite ne touchera donc jamais cette entrée.
 *
 * ⚠️ Deux façons d'exprimer la quantité, et le sens de `quantity` en dépend
 * (specs §6.3, « au choix en unité de base ou via une portion ») :
 *   - sans `portionId` : `quantity` est en **unité de base** (150 = 150 g) ;
 *   - avec `portionId` : `quantity` est un **nombre de portions** (2 = deux
 *     tranches), et le serveur le convertit en unité de base.
 */
export interface FoodEntryInput {
  type: 'food';
  foodId: number;
  /** En g/ml sans `portionId`, en nombre de portions avec. */
  quantity: number;
  /** Portion choisie parmi celles de l'aliment. */
  portionId?: number;
}

/**
 * Ajustement d'un ingrédient pour **cette occurrence uniquement** (specs §6.5).
 *
 * ⚠️ La recette enregistrée n'est **jamais** modifiée par cet ajustement : il ne
 * décrit que ce qui a réellement été mis dans l'assiette ce jour-là, et il
 * n'atteint que `log_entry_ingredient` (§4.5).
 */
export interface RecipeEntryIngredientInput {
  /** `recipe_ingredient.id` — doit appartenir à la recette ajoutée. */
  ingredientId: number;
  /** Quantité **en unité de base de l'aliment** (g/ml). */
  quantity: number;
}

/**
 * Ajout d'une recette au journal (specs §6.5) — étape 7.
 *
 * ⚠️ **Aucune macro n'est transmise**, ni pour l'entrée ni pour ses lignes. Le
 * client désigne la recette, la quantité consommée et, s'il y a lieu, les
 * quantités d'ingrédients ajustées ; le serveur lit `food.*_100` pour chaque
 * ligne, fige les macros de chacune, puis en fait la somme pour le total du
 * `log_entry` parent (§4.1, §5.1).
 *
 * ⚠️ `quantity` s'interprète selon le rendement de la recette (§4.3) :
 *   - `yieldType: 'portions'` → **nombre de portions** consommées ;
 *   - `yieldType: 'weight'`   → **poids consommé**, en grammes.
 * Dans les deux cas, le rapport `quantity / yieldValue` donne l'échelle
 * appliquée par défaut aux quantités d'ingrédients.
 *
 * ⚠️ `ingredients` est **facultatif et partiel** : chaque ligne omise garde la
 * quantité de la recette mise à l'échelle. Une ligne à `0` retire l'ingrédient
 * de cette occurrence — « je n'ai pas mis d'oignon ce soir ».
 */
export interface RecipeEntryInput {
  type: 'recipe';
  recipeId: number;
  /** Portions consommées, ou poids en grammes — selon `yieldType`. */
  quantity: number;
  /** Ajustements de cette occurrence uniquement. */
  ingredients?: RecipeEntryIngredientInput[];
}

/**
 * Entrée à écrire. Union discriminée par `type` — les trois voies qui écrivent
 * en base (specs §6.3) partagent route, service et écran d'ajout : aucune n'est
 * traitée comme un cas particulier.
 */
export type LogEntryInput = QuickEntryInput | FoodEntryInput | RecipeEntryInput;

/**
 * Corps de `PATCH /api/entries/:id/ingredients` — ré-édition d'un bloc recette
 * déjà journalisé (specs §6.5-d, archi §4.5 dernier alinéa).
 *
 * ⚠️ Route **dédiée**, distincte de `PATCH /api/entries/:id` : elle ne touche
 * que les lignes du bloc et le total de son entrée parente. Elle ne relit pas la
 * recette source, qui a pu changer ou disparaître entre-temps, et n'a aucun
 * effet sur les autres entrées du journal.
 */
export interface AdjustEntryIngredientsBody {
  /** Lignes à corriger. Une ligne omise garde sa quantité ; `0` la retire. */
  ingredients: AdjustEntryIngredientInput[];
}

/** Une ligne du bloc, désignée par l'`id` de `log_entry_ingredient`. */
export interface AdjustEntryIngredientInput {
  id: number;
  /** Nouvelle quantité, en unité de base. `0` retire la ligne du bloc. */
  quantity: number;
}

/** Corps de `POST /api/meals/:id/entries`. */
export type CreateLogEntryBody = LogEntryInput & ClientIdentified;

/** Corps de `PATCH /api/entries/:id` — remplace intégralement l'entrée. */
export type UpdateLogEntryBody = LogEntryInput;

/** Corps de `POST /api/days/:date/meals`. */
export interface CreateMealBody extends ClientIdentified {
  name: string;
  /** Cibles propres au repas. Toutes à zéro si omises. */
  targets?: MacroSet;
}

/** Corps de `PATCH /api/meals/:id`. Champs omis = inchangés. */
export interface UpdateMealBody {
  name?: string;
  targets?: MacroSet;
  /** Marque ce repas comme réutilisable depuis l'écran d'ajout (`SavedMealDTO`). */
  isFavorite?: boolean;
}

/** Corps de `POST /api/days/:date/apply-template`. */
export interface ApplyTemplateBody {
  templateId: number;
}

/* --------------------------------------------------------------------------
 * Aliments — base personnelle (§4.3, specs §6.4) — étape 6
 * ----------------------------------------------------------------------- */

/**
 * Portion nommée d'un aliment : « 1 tranche » = 32 g.
 *
 * `quantity` est **toujours** exprimée dans l'unité de base de l'aliment, pas
 * dans une unité propre à la portion : c'est ce qui permet au serveur de
 * ramener n'importe quelle saisie à des grammes sans table de conversion.
 */
export interface FoodPortionDTO {
  id: number;
  /** Libellé affiché, tel que saisi (« 1 tranche », « 1 bol »). */
  label: string;
  /** Valeur en unité de base (g ou ml). */
  quantity: number;
  position: number;
}

/**
 * Aliment de la base personnelle (§4.3).
 *
 * ⚠️ `source` est la **provenance à la création**, pas un état : une fiche
 * importée depuis Open Food Facts garde `openfoodfacts` même après correction
 * de ses macros. Ce qui donne la priorité en recherche (specs §7.2), ce n'est
 * pas cette colonne mais le fait d'être **en base** — tout ce que contient cette
 * table est la base personnelle, quelle que soit son origine.
 */
export interface FoodDTO {
  id: number;
  name: string;
  brand: string | null;
  barcode: string | null;
  source: FoodSource;
  baseUnit: BaseUnit;
  /** Macros de référence, pour 100 g ou 100 ml (§4.3). */
  per100: MacroSet;
  isFavorite: boolean;
  /** Code Open Food Facts d'origine, si la fiche en a été copiée. */
  offCode: string | null;
  portions: FoodPortionDTO[];
  createdAt: number;
  updatedAt: number;
}

/** Portion en écriture — pas d'`id` : la liste est remplacée en bloc. */
export interface FoodPortionInput {
  label: string;
  quantity: number;
}

/**
 * Corps de `POST /api/foods` et `PUT /api/foods/:id`.
 *
 * Les macros sont celles **pour 100 g / 100 ml**. Ce n'est pas une entorse au
 * §5.1 (« le serveur recalcule toujours ») : ici on décrit un aliment de
 * référence, pas une entrée de journal. Le total d'une entrée reste calculé par
 * le serveur à partir de ces valeurs et de la quantité consommée.
 */
export interface FoodInput extends ClientIdentified {
  name: string;
  brand?: string | null;
  barcode?: string | null;
  baseUnit: BaseUnit;
  per100: MacroSet;
  isFavorite?: boolean;
  portions?: FoodPortionInput[];
  /** Renseigné quand la fiche est copiée depuis Open Food Facts (specs §6.4). */
  offCode?: string | null;
}

/** Corps de `PATCH /api/foods/:id`. Champs omis = inchangés. */
export interface UpdateFoodBody {
  name?: string;
  brand?: string | null;
  barcode?: string | null;
  baseUnit?: BaseUnit;
  per100?: MacroSet;
  isFavorite?: boolean;
  /** Fournie = la liste des portions est **remplacée en bloc**. */
  portions?: FoodPortionInput[];
}

/* --------------------------------------------------------------------------
 * Open Food Facts (§5.2, specs §6.4) — étape 6
 * ----------------------------------------------------------------------- */

/**
 * Produit Open Food Facts **normalisé** par `services/off.ts`.
 *
 * ⚠️ Ce n'est pas la charge utile d'OFF : elle est retaillée côté serveur (clés
 * instables, unités variables, énergie tantôt en kJ tantôt en kcal), et tout
 * produit aux macros absentes ou aberrantes est **rejeté** plutôt qu'importé
 * (§5.2). Ce qui arrive ici est donc toujours exploitable tel quel.
 *
 * ⚠️ Ces macros restent **peu fiables par défaut** (specs §6.4) : l'écran
 * d'ajout doit les afficher et les laisser éditer avant validation.
 */
export interface OffProductDTO {
  /** Code-barres, et clé du cache local (§4.3, `off_cache`). */
  code: string;
  name: string;
  brand: string | null;
  baseUnit: BaseUnit;
  /** Macros pour 100 g ou 100 ml. */
  per100: MacroSet;
  /** Portion annoncée par OFF, ramenée en unité de base. `null` si absente. */
  servingQuantity: number | null;
  /** Libellé de cette portion tel qu'OFF l'écrit (« 30 g », « 1 pot »). */
  servingLabel: string | null;
  /** Contenance du paquet (« 450 g »), purement informative. */
  quantityLabel: string | null;
  imageUrl: string | null;
  /** Date de la mise en cache locale (§5.2). */
  fetchedAt: number;
}

/**
 * Disponibilité d'Open Food Facts pour cette requête.
 *
 * ⚠️ Une recherche ne doit **jamais** échouer parce qu'OFF est indisponible :
 * la base personnelle répond toujours, et ce champ dit seulement si la moitié
 * distante a pu être interrogée.
 */
export type OffStatus = 'ok' | 'unavailable';

/**
 * Recherche unifiée (specs §6.3-b et §7.2).
 *
 * `personal` vient **toujours en premier** et `off` en est déjà déduplique :
 * un produit dont le code-barres est déjà en base personnelle n'apparaît pas
 * une seconde fois côté OFF. La distinction visuelle des deux listes est à la
 * charge du client, qui les reçoit séparées pour ne pas avoir à la deviner.
 */
export interface FoodSearchResponse {
  query: string;
  personal: FoodDTO[];
  /**
   * Recettes personnelles correspondantes (nom ou tag) — étape 7.
   *
   * ⚠️ Extension assumée du §6.3-b, qui ne parle que d'aliments. Sans elle, une
   * recette ni favorite ni récente serait **inatteignable depuis l'écran
   * d'ajout**, l'accès rapide ne montrant que deux listes courtes.
   */
  recipes: RecipeDTO[];
  off: OffProductDTO[];
  offStatus: OffStatus;
}

/** Résultat d'un scan / d'un lookup par code-barres (specs §6.4). */
export interface BarcodeLookupResponse {
  code: string;
  /**
   * Fiche personnelle portant ce code-barres — **prioritaire** (specs §7.2).
   * Quand elle existe, le client n'a rien à demander de plus.
   */
  personal: FoodDTO | null;
  /** Produit OFF, `null` si inconnu ou rejeté à la normalisation. */
  off: OffProductDTO | null;
  offStatus: OffStatus;
}

/* --------------------------------------------------------------------------
 * Accès rapide (specs §6.3-a, archi §6.2) — étape 6
 * ----------------------------------------------------------------------- */

/**
 * Charge utile de l'écran « Accès rapide » — **un seul endpoint** (§6.2), parce
 * que c'est le premier écran du chemin critique et qu'il ne doit pas coûter
 * trois allers-retours.
 *
 * ⚠️ `recentFoods` est trié par **récence d'usage réel** — la date de la
 * dernière entrée de journal qui référence l'aliment — et non par date de
 * création. C'est l'un des rares usages légitimes de `log_entry.food_id`, que le
 * §4.1 réserve explicitement à « la ré-édition et aux statistiques d'usage ».
 */
/**
 * Repas déjà composé, proposé à la réutilisation — ajouté après usage.
 *
 * ⚠️ **Ce n'est pas un objet de référentiel.** C'est une *occurrence* passée :
 * le petit-déjeuner d'un jour précis, avec ses entrées telles qu'elles ont été
 * enregistrées. Il n'existe ni table de repas types, ni « repas » à gérer dans
 * la Bibliothèque — le journal est déjà l'endroit où vivent les repas, et
 * inventer un second modèle aurait obligé à le tenir synchronisé avec le
 * premier.
 *
 * ⚠️ `macros` est la somme des entrées, **calculée à la lecture**. Elle ne sert
 * qu'à donner un ordre de grandeur avant de choisir ; ce qui sera réellement
 * écrit reste décidé par le serveur au moment de la copie.
 */
export interface SavedMealDTO {
  /** `meal.id` de l'occurrence d'origine — c'est elle que la copie recopie. */
  id: number;
  name: string;
  /** Date de la journée où ce repas a été composé, `YYYY-MM-DD`. */
  date: string;
  isFavorite: boolean;
  /**
   * Libellés des entrées, dans l'ordre. De quoi reconnaître un repas d'un coup
   * d'œil : deux « Petit-déjeuner » ne se distinguent que par leur contenu.
   */
  items: string[];
  /** Somme des macros des entrées. */
  macros: MacroSet;
}

/**
 * Corps de `POST /api/meals/:id/copy` — recopie les entrées d'un repas passé
 * dans celui-ci.
 *
 * ⚠️ Aucune macro, aucun libellé : le serveur lit lui-même le repas source. Un
 * client qui enverrait le contenu à copier pourrait le falsifier (§5.1).
 */
export interface CopyMealBody extends ClientIdentified {
  sourceMealId: number;
}

export interface QuickAccessResponse {
  recentFoods: FoodDTO[];
  favoriteFoods: FoodDTO[];
  /**
   * Derniers repas composés, **dédoublonnés par composition** : manger le même
   * petit-déjeuner trente jours de suite donne une ligne, pas trente.
   */
  recentMeals: SavedMealDTO[];
  /** Repas marqués réutilisables, le plus récent d'abord. Jamais dédoublonnés. */
  favoriteMeals: SavedMealDTO[];
  /**
   * Recettes récemment journalisées, même tri par usage réel que `recentFoods`.
   *
   * ⚠️ **Recettes complètes, ingrédients compris** — et non un résumé, contrairement
   * à ce que l'étape 6 avait réservé. L'écran d'ajustement des ingrédients
   * (specs §6.5) en a besoin dès le tap : servir un résumé coûterait un
   * aller-retour au milieu du chemin critique, exactement ce que le §6.2
   * cherche à éviter en n'exigeant qu'un seul endpoint.
   */
  recentRecipes: RecipeDTO[];
  favoriteRecipes: RecipeDTO[];
}


/* --------------------------------------------------------------------------
 * Recettes (§4.3, specs §6.5) — étape 7
 * ----------------------------------------------------------------------- */

/**
 * Ingrédient d'une recette, tel que lu.
 *
 * ⚠️ **`macros` est calculé à la lecture**, depuis `food.*_100` et la quantité —
 * jamais stocké. C'est l'exact opposé du journal (§4.1), et c'est voulu : une
 * recette est un objet du **référentiel**, pas un historique. Corriger la fiche
 * d'un aliment doit se répercuter sur les recettes qui l'emploient (specs §6.5 :
 * « macros toujours calculées depuis les ingrédients, jamais saisies »), et ne
 * doit **rien** changer aux entrées de journal déjà écrites.
 */
export interface RecipeIngredientDTO {
  id: number;
  foodId: number;
  /** `nom — marque`, composé à la lecture depuis la fiche courante. */
  label: string;
  /** En unité de base sans `portionId`, en nombre de portions avec. */
  quantity: number;
  /** `g` / `ml`, ou `portion` quand une portion nommée est utilisée. */
  unit: string;
  /** Portion nommée choisie à la saisie, si l'ingrédient n'est pas en g/ml. */
  portionId: number | null;
  /** Libellé courant de cette portion (« 1 tranche »), `null` sinon. */
  portionLabel: string | null;
  /** Quantité ramenée en unité de base — c'est elle qui sert au calcul. */
  baseQuantity: number;
  baseUnit: BaseUnit;
  /** Macros de cet ingrédient pour la quantité indiquée. **Calculées.** */
  macros: MacroSet;
  position: number;
}

/**
 * Recette (§4.3, specs §6.5).
 *
 * ⚠️ **Aucune macro n'est stockée ni acceptée en entrée.** Les trois jeux
 * ci-dessous sont calculés à chaque lecture depuis `recipe_ingredient × food` ;
 * `RecipeInput` n'offre aucun champ où en écrire un, et le corps est en
 * `strict` — un `totals` envoyé est refusé (400), pas ignoré.
 */
export interface RecipeDTO {
  id: number;
  name: string;
  /** Étapes de préparation, texte libre ordonné — une étape par ligne. */
  steps: string | null;
  /** Tags, triés, sans doublon (§4.3, `recipe_tag`). */
  tags: string[];
  yieldType: RecipeYieldType;
  /** Nombre de portions, ou poids total en grammes — selon `yieldType`. */
  yieldValue: number;
  isFavorite: boolean;
  ingredients: RecipeIngredientDTO[];
  /** Macros de la recette **entière**. Calculées. */
  totals: MacroSet;
  /** Macros d'une portion. `null` si le rendement est exprimé en poids. */
  perPortion: MacroSet | null;
  /** Macros pour 100 g de plat fini. `null` si le rendement est en portions. */
  per100: MacroSet | null;
  /**
   * Somme des quantités d'ingrédients ramenées en unité de base.
   *
   * Purement informatif, et volontairement distinct de `yieldValue` : un plat
   * perd de l'eau à la cuisson, et c'est le poids **fini** qui sert à ramener à
   * 100 g. L'éditeur s'en sert pour proposer une valeur de départ.
   */
  ingredientsQuantity: number;
  /** Écart 4/4/9 des macros totales — indicatif (§5.3). */
  kcalGap: KcalGap;
  createdAt: number;
  updatedAt: number;
}

/**
 * Ingrédient en écriture — pas d'`id` : la liste est remplacée en bloc, comme
 * les repas types d'un modèle et les portions d'un aliment.
 *
 * ⚠️ Ni `unit` ni macros : le serveur déduit l'unité de la fiche d'aliment
 * (`portion` s'il y a une portion, l'unité de base sinon) et calcule tout le
 * reste. Il n'y a donc rien à contredire.
 */
export interface RecipeIngredientInput {
  foodId: number;
  /** En unité de base sans `portionId`, en nombre de portions avec. */
  quantity: number;
  portionId?: number | null;
}

/**
 * Corps de `POST /api/recipes`.
 *
 * ⚠️ **Aucun champ de macro**, à aucun niveau. C'est la traduction littérale du
 * §6.5 des specs (« macros toujours calculées depuis les ingrédients, jamais
 * saisies ») : la règle n'est pas seulement respectée, elle est inexprimable
 * autrement.
 */
export interface RecipeInput extends ClientIdentified {
  name: string;
  steps?: string | null;
  tags?: string[];
  yieldType: RecipeYieldType;
  /** Strictement positif : c'est le diviseur du calcul par portion / pour 100 g. */
  yieldValue: number;
  isFavorite?: boolean;
  ingredients: RecipeIngredientInput[];
}

/** Corps de `PATCH /api/recipes/:id`. Champs omis = inchangés. */
export interface UpdateRecipeBody {
  name?: string;
  steps?: string | null;
  /** Fournis = la liste des tags est **remplacée en bloc**. */
  tags?: string[];
  yieldType?: RecipeYieldType;
  yieldValue?: number;
  isFavorite?: boolean;
  /** Fournis = la liste des ingrédients est **remplacée en bloc**. */
  ingredients?: RecipeIngredientInput[];
}

/* --------------------------------------------------------------------------
 * Poids (§4.6, specs §6.6) — étape 8
 * ----------------------------------------------------------------------- */

/**
 * Plages du graphique d'évolution (specs §6.6 : « 30 j, 90 j, 1 an, tout »).
 *
 * ⚠️ La plage ne borne que ce qui est **renvoyé**, jamais ce qui est **calculé** :
 * la moyenne lissée et la tendance sont toujours établies sur l'historique
 * complet, puis découpées. Découper d'abord donnerait une courbe lissée fausse
 * sur les six premiers jours de chaque plage — la fenêtre y manquerait des
 * pesées qui existent pourtant juste avant la borne.
 */
export type WeightRange = '30d' | '90d' | '1y' | 'all';

/**
 * Pesée (§4.6, specs §5.2) — une par jour, la date est la clé.
 *
 * ⚠️ **Aucune immuabilité ici, et c'est délibéré.** Le §4.1 fige les macros d'une
 * entrée de journal parce qu'elles décrivent un fait passé reconstitué depuis un
 * référentiel qui, lui, continue de bouger. Une pesée n'a rien de tel derrière
 * elle : elle *est* la donnée, et les specs §6.6 demandent explicitement un
 * « historique consultable et **corrigeable** ». Corriger une pesée passée est un
 * usage normal, pas une exception — ne pas transposer ici le réflexe du §4.1.
 */
export interface WeightEntryDTO {
  /** `YYYY-MM-DD`. */
  date: string;
  kg: number;
  /** Heure de la saisie courante, epoch ms — réécrite à chaque correction. */
  recordedAt: number;
}

/** Corps de `PUT /api/weight/:date`. La date est dans le chemin, pas ici. */
export interface WeightEntryInput {
  kg: number;
}

/**
 * Point du graphique : une pesée, avec sa moyenne lissée.
 *
 * ⚠️ Il n'y a de point **que les jours pesés** — aucun jour n'est interpolé. Un
 * trou dans l'historique se voit donc dans la courbe, ce qui est l'information
 * juste : on n'invente pas une pesée qui n'a pas eu lieu.
 */
export interface WeightPointDTO {
  date: string;
  /** Poids brut de ce jour. */
  kg: number;
  /**
   * Moyenne des pesées des **7 jours calendaires** finissant à `date`, incluse.
   *
   * ⚠️ Sept *jours*, pas sept *pesées* : sauter trois jours ne fait pas remonter
   * la fenêtre dix jours en arrière. Une fenêtre qui ne contient qu'une pesée
   * vaut donc cette pesée — c'est le cas des premiers jours d'un historique.
   */
  smoothed: number;
}

/**
 * Objectif de poids (§4.6, specs §5.2 et §6.6).
 *
 * ⚠️ Table **historisée** : changer d'objectif insère une ligne, n'en modifie
 * jamais une. Le plus récent fait foi. Il n'existe donc volontairement pas de
 * `PATCH` — voir `routes/weight.ts`.
 */
export interface WeightGoalDTO {
  id: number;
  targetKg: number;
  /** Rythme visé, **signé** : négatif pour une perte, positif pour une prise. */
  rateKgWeek: number;
  createdAt: number;
}

/**
 * Corps de `POST /api/weight/goals`. Insère une ligne de plus.
 *
 * 🔴 **`clientId` dédoublonne le rejeu, pas l'objectif** (V2 §2.2). Poser deux
 * fois le même objectif reste **deux lignes** — c'est la date à laquelle on l'a
 * reconduit, et c'est ce que la table historisée existe pour tenir. Ce que le
 * `clientId` empêche, c'est qu'une **même** requête réémise (file de
 * synchronisation, double tap) en écrive une seconde. Tirer un identifiant
 * neuf **à chaque geste** est donc la façon correcte de s'en servir.
 */
export interface WeightGoalInput extends ClientIdentified {
  targetKg: number;
  rateKgWeek: number;
}

/**
 * Ce que l'écran Progression affiche en tête (specs §6.6).
 *
 * ⚠️ **Rien de tout ceci n'est stocké** (§4.6 : « le stockage introduirait un
 * risque d'incohérence »). Les valeurs sont recalculées à chaque lecture, ce qui
 * est aussi ce qui garantit qu'une correction d'historique ne laisse jamais une
 * moyenne périmée derrière elle.
 */
export interface WeightSummaryDTO {
  /** Dernière pesée connue, quelle qu'en soit la date. `null` si aucune. */
  latest: WeightEntryDTO | null;
  /** Moyenne lissée 7 jours à la date de `latest`. `null` si aucune pesée. */
  smoothed: number | null;
  /**
   * Tendance en kg/semaine, **signée**. `null` tant que l'historique est trop
   * court pour qu'une pente veuille dire quelque chose — voir `trendWindowDays`,
   * `trendSampleCount` et le service `weightTrend.ts`.
   */
  trendKgWeek: number | null;
  /** Fenêtre glissante sur laquelle la tendance est établie, en jours. */
  trendWindowDays: number;
  /** Nombre de pesées effectivement employées dans cette fenêtre. */
  trendSampleCount: number;
  /**
   * `tendance réelle − rythme visé`, en kg/semaine. `null` sans objectif ou sans
   * tendance. Positif = on dérive vers le haut par rapport au rythme visé.
   */
  rateGapKgWeek: number | null;
  /** `poids lissé − poids cible`. `null` sans objectif ou sans pesée. */
  toTargetKg: number | null;
  /**
   * Projection vers l'objectif **au rythme actuel** (V2 §6.2, étape 9).
   *
   * 🔴 **`null` bien plus souvent qu'on ne le croit, et c'est le sujet.** Elle
   * n'est publiée que sous les **mêmes planchers que la tendance** (3 pesées
   * étalées sur 7 jours) **et** à tendance non nulle : projeter sur une
   * tendance nulle est une division par zéro déguisée, qui afficherait
   * « jamais » ou une date absurde selon l'arrondi.
   *
   * ⚠️ Et elle ne se publie pas non plus quand la tendance **s'éloigne** de la
   * cible : la date d'arrivée serait dans le passé. `projectionHold` dit
   * laquelle des cinq raisons s'applique.
   */
  projection: WeightProjectionDTO | null;
  /**
   * Pourquoi il n'y a pas de projection — `null` quand il y en a une.
   *
   * ⚠️ **Un motif porté par la réponse, jamais un silence** : c'est la
   * discipline d'`offStatus: 'unavailable'` (étape 6) et des huit motifs de
   * retenue de la progression (étape 7). Une projection absente sans motif est
   * indistinguable d'une règle qui n'a pas tourné.
   */
  projectionHold: WeightProjectionHold | null;
}

/**
 * Les cinq raisons de ne pas projeter.
 *
 * | Motif | Situation |
 * |-|-|
 * | `no_goal` | aucun objectif de poids n'est posé |
 * | `no_trend` | moins de 3 pesées, ou étalées sur moins de 7 jours |
 * | `flat` | tendance nulle — la division par zéro déguisée |
 * | `diverging` | la tendance éloigne de la cible : la date serait dans le passé |
 * | `reached` | l'objectif est déjà atteint, à la tolérance près |
 */
export type WeightProjectionHold = 'no_goal' | 'no_trend' | 'flat' | 'diverging' | 'reached';

/** Quand l'objectif serait atteint si la tendance actuelle se maintenait. */
export interface WeightProjectionDTO {
  /**
   * Date estimée, `YYYY-MM-DD`.
   *
   * ⚠️ **Une date, pas une durée en semaines** (§6.2 : « elle affiche une
   * date »). « Dans 14 semaines » demande un calcul mental pour savoir de quel
   * mois on parle ; une date se lit.
   */
  date: string;
  /** Jours d'ici là, depuis la date de la dernière pesée. */
  days: number;
  /**
   * Le poids lissé de départ et la cible, pour que l'écran puisse phraser sans
   * refaire la soustraction.
   */
  fromKg: number;
  targetKg: number;
}

/**
 * Réponse de `GET /api/weight` **et de toutes ses mutations**.
 *
 * Même parti pris qu'au journal (§4.5) : une mutation renvoie l'état complet
 * plutôt qu'un 204. Ici la raison est plus forte encore — corriger une seule
 * pesée déplace la moyenne lissée de sept points et la tendance, si bien qu'un
 * 204 obligerait le client à redemander presque tout ce qu'il vient d'écrire.
 */
export interface WeightResponse {
  /** Date du jour selon le `TZ` du serveur (§9) — c'est elle qui fait foi. */
  today: string;
  /** Pesée du jour. `null` si elle n'a pas encore été saisie. */
  todayEntry: WeightEntryDTO | null;
  range: WeightRange;
  summary: WeightSummaryDTO;
  /** Objectif courant = la ligne la plus récente. `null` si jamais défini. */
  goal: WeightGoalDTO | null;
  /** Pesées de la plage demandée, par date croissante. */
  points: WeightPointDTO[];
  /** Nombre total de pesées en base, toutes plages confondues. */
  totalEntries: number;
}

/* --------------------------------------------------------------------------
 * Exercices (V2, étape 4) — `ARCHITECTURE_V2.md` §1.1 et §4.1, specs §12.1
 * ----------------------------------------------------------------------- */

/**
 * Provenance d'un exercice.
 *
 * ⚠️ **Une provenance, jamais un état** — même règle qu'à `FoodSource`
 * (étape 6). Un exercice importé puis entièrement corrigé garde `base` : ce qui
 * lui donne la priorité, ce n'est pas cette valeur, c'est le fait d'être en
 * base.
 */
export type ExerciseSource = 'base' | 'perso';

/**
 * Taxonomie du jeu importé, reprise telle quelle (V2 §1.1).
 *
 * 🔴 **Ne pas en inventer une autre.** Le §1.1 est explicite : « inventer une
 * taxonomie propre imposerait une table de correspondance à tenir, et perdrait
 * de l'information à l'import ». Ces valeurs sont celles du `schema.json` de
 * `free-exercise-db`, vérifiées contre les 873 exercices du commit épinglé par
 * `infra/fetch-exercises.sh`.
 *
 * ⚠️ `category`, `equipment` et les muscles sont typés **`string`** et non en
 * union fermée, alors que le jeu les énumère. C'est délibéré : l'utilisateur
 * crée ses propres exercices (specs §12.1 : « extensible par l'utilisateur »),
 * et une machine propre à sa salle n'a aucune raison de tomber dans une liste
 * écrite ailleurs. Les trois champs ci-dessous, eux, restent fermés parce
 * qu'ils décrivent la **mécanique** du mouvement, qui ne dépend pas du matériel.
 */
export type ExerciseForce = 'push' | 'pull' | 'static';
export type ExerciseLevel = 'beginner' | 'intermediate' | 'expert';
export type ExerciseMechanic = 'compound' | 'isolation';

/**
 * Un exercice, tel qu'il s'affiche en liste.
 *
 * 🔴 **`instructions` en est absent, et c'est une mesure, pas un oubli.** Le jeu
 * complet pèse 869 Ko, dont **571 Ko de seules instructions** (66 %). Cette
 * liste est répliquée côté client pour que la recherche fonctionne hors ligne
 * (§3.1) et elle est **persistée** : la réécrire à chaque mutation du journal
 * coûterait trois fois plus pour un texte que la liste n'affiche pas.
 * `ExerciseDTO` le porte, et il se charge à la consultation — exactement la
 * politique des images (§1.1 : « mises en cache à la consultation »).
 */
export interface ExerciseSummaryDTO {
  /**
   * 🔴 **L'adresse de la ressource** (V2 §2.3-b), pas une commodité : les routes
   * sont `/api/exercises/:clientId`. C'est ce qui permet de composer hors ligne
   * sans place-titre.
   */
  clientId: string;
  /**
   * ⚠️ Toujours renvoyé, mais **informatif** : il reste la clé primaire et le
   * pivot des jointures de l'étape 5, il n'adresse plus rien.
   */
  id: number;
  name: string;
  source: ExerciseSource;
  category: string;
  force: ExerciseForce | null;
  level: ExerciseLevel;
  mechanic: ExerciseMechanic | null;
  equipment: string | null;
  primaryMuscles: string[];
  secondaryMuscles: string[];
  /**
   * Dossier d'images, ou `null` quand l'exercice n'en a pas.
   *
   * ⚠️ **Un dossier, pas un fichier** (V2 §11) : y ajouter une vidéo le jour où
   * une source acceptable existe ne demandera aucune migration. Les images se
   * lisent en `/exercises/<mediaKey>/<n>.jpg`.
   *
   * ⚠️ `null` est le cas **nominal** d'un exercice personnel — une machine
   * propre à une salle (§1.1). L'écran ne réserve alors aucun espace vide.
   */
  mediaKey: string | null;
  /** Nombre d'images disponibles sous `mediaKey`. `0` quand il n'y en a pas. */
  imageCount: number;
  isFavorite: boolean;
  createdAt: number;
  updatedAt: number;
}

/** Un exercice complet — le résumé, plus ce que seul l'écran de détail affiche. */
export interface ExerciseDTO extends ExerciseSummaryDTO {
  /**
   * Étapes du mouvement, une par ligne — même forme que `RecipeDTO.steps`
   * (étape 7). Le jeu importé les livre en tableau ; elles sont jointes par des
   * retours à la ligne à l'import, parce que c'est ainsi qu'on les saisit.
   */
  instructions: string | null;
}

/**
 * Corps de `PUT /api/exercises/:clientId` — création **ou** remplacement.
 *
 * 🔴 **Un `PUT`, pas un `POST`** (V2 §2.3-b) : « puisque le client possède
 * l'identité, créer c'est écrire une ressource nommée — idempotent par
 * construction, sans lecture préalable ni table de dédoublonnage ». Exactement
 * le raisonnement de `PUT /api/weight/:date` à l'étape 8.
 *
 * ⚠️ **Aucun champ `source`.** Elle se déduit : une ressource qui n'existait pas
 * naît `perso`, une ressource existante garde la sienne. Un `source` envoyé est
 * refusé 400 (corps `strict`) — c'est ce qui rend « provenance, jamais un état »
 * vrai par construction plutôt que par discipline.
 *
 * ⚠️ **Aucun champ `mediaKey` non plus.** Les images sont servies depuis un
 * dossier déposé par `infra/fetch-exercises.sh` : laisser le client nommer ce
 * dossier reviendrait à le laisser désigner n'importe quel chemin. Un exercice
 * personnel n'a pas d'image, et c'est le cas nominal (§1.1).
 */
export interface ExerciseInput {
  name: string;
  category: string;
  force?: ExerciseForce | null;
  level: ExerciseLevel;
  mechanic?: ExerciseMechanic | null;
  equipment?: string | null;
  primaryMuscles: string[];
  secondaryMuscles?: string[];
  instructions?: string | null;
  isFavorite?: boolean;
}

/**
 * Corps de `PATCH /api/exercises/:clientId` — modification partielle.
 *
 * ⚠️ **Distinct de `PUT`, et les deux servent.** Le `PUT` remplace la ressource
 * entière : s'en servir pour basculer une étoile obligerait l'écran de liste à
 * porter tout l'exercice, instructions comprises — c'est-à-dire précisément ce
 * que `ExerciseSummaryDTO` ne transporte pas. Le favori se pose donc ici.
 */
export interface UpdateExerciseBody {
  name?: string;
  category?: string;
  force?: ExerciseForce | null;
  level?: ExerciseLevel;
  mechanic?: ExerciseMechanic | null;
  equipment?: string | null;
  primaryMuscles?: string[];
  secondaryMuscles?: string[];
  instructions?: string | null;
  isFavorite?: boolean;
}

/*
 * ⚠️ **Aucun DTO de facettes, et pas par oubli.** Les recettes ont
 * `GET /api/recipes/tags` (étape 7) pour peupler leur filtre ; les exercices
 * n'en ont pas besoin, parce que la liste complète est **déjà** répliquée côté
 * client pour la recherche hors ligne (§3.1). Les catégories, le matériel et
 * les muscles employés s'en déduisent en une passe — voir
 * `web/src/lib/localSearch.ts`.
 *
 * Deux propriétés gratuites, qu'une route n'aurait pas données : les filtres
 * fonctionnent **sans réseau**, et ils ne peuvent pas se désaccorder de la
 * liste qu'ils filtrent, puisqu'ils en sortent.
 */

/* --------------------------------------------------------------------------
 * Routines (V2, étape 5) — `ARCHITECTURE_V2.md` §4.1 et §4.3, specs §12.2
 * ----------------------------------------------------------------------- */

/**
 * Nature d'un bloc (§4.3, à ne pas réinterpréter).
 *
 * | `kind`     | Contenu   | Déroulé en salle |
 * |------------|-----------|------------------|
 * | `single`   | une ligne | les séries à la suite, repos entre chacune |
 * | `superset` | N lignes  | une série de chaque ligne **sans repos**, puis le repos du bloc ; `rounds` est **ignoré**, le nombre de tours est celui des séries |
 * | `circuit`  | N lignes  | un tour = une série de chaque ligne, `rounds` tours, repos du bloc entre les tours |
 *
 * 🔴 **Le bloc existe parce que superset et circuit sont des propriétés du
 * GROUPE, pas de la ligne** (§4.1). Les porter sur la ligne
 * (`superset_group_id`) obligerait à reconstituer le groupe à chaque lecture et
 * à en maintenir la cohérence à chaque écriture ; une table de blocs le dit une
 * fois, et rend le réordonnancement trivial.
 */
export type RoutineBlockKind = 'single' | 'superset' | 'circuit';

/**
 * Type d'une série (specs §12.2).
 *
 * ⚠️ `amrap` est la « série longue jusqu'à l'échec » des specs — *as many reps
 * as possible*. La plage de reps reste renseignée : c'est la cible, pas un
 * plafond.
 */
export type SetKind = 'warmup' | 'work' | 'drop' | 'amrap';

/**
 * Règle de progression (§4.4).
 *
 * 🔴 **Une seule règle, la double progression, paramétrée par son incrément.
 * Aucun moteur de règles** : ce serait un langage à concevoir, à valider et à
 * expliquer, pour un utilisateur unique qui a exactement une façon de
 * progresser. Une seconde règle sera une seconde valeur ici — la colonne est
 * déjà là, et c'est tout ce qu'il fallait prévoir.
 *
 * ⚠️ **Saisie à l'étape 5, consultée à l'étape 7.** Rien ne la lit encore :
 * `services/progression.ts` n'existe pas, et la charge proposée se calculera
 * **à la lecture** depuis l'historique des séries réalisées, sans jamais
 * réécrire `targetWeightKg`.
 */
export type ProgressionKind = 'none' | 'double';

/**
 * Une ligne de routine : un exercice, et ce qu'on vise dessus.
 *
 * 🔴 **`exerciseName` est joint à la lecture, jamais copié — c'est l'inverse
 * exact de `log_entry.label`.** La distinction est celle que l'étape 7 a
 * établie entre référentiel et historique, et le §4.1 la reprend nommément pour
 * les routines : une routine dont l'exercice a disparu ne veut plus rien dire
 * (d'où le `RESTRICT`), alors qu'une entrée de journal garde ses macros figées.
 * Renommer un exercice doit donc **changer** ce que la routine affiche — comme
 * corriger un aliment change les macros des recettes qui l'emploient (specs
 * §6.5). C'est `actual_set.exercise_name` (étape 6) qui copiera, parce que lui
 * est de l'historique.
 */
export interface RoutineLineDTO {
  /** 🔴 L'adresse de la ligne (V2 §2.3-b), pas une commodité. */
  clientId: string;
  /** Informatif : clé primaire et pivot des jointures, il n'adresse rien. */
  id: number;
  /** Rang dans le bloc, renuméroté de 0 à n−1 par le serveur. */
  position: number;
  /** Adresse de l'exercice visé — jamais son `id` entier (§2.3-b). */
  exerciseClientId: string;
  /** Joint à la lecture. Cf. l'en-tête : ce n'est **pas** une copie. */
  exerciseName: string;
  /** Dossier d'images de l'exercice, pour le préchargement ciblé du §1.1. */
  exerciseMediaKey: string | null;
  exerciseImageCount: number;
  sets: number;
  /**
   * Plage de répétitions visée — `null` sur une ligne **en durée**.
   *
   * ⚠️ **Reps fixes = `repMin === repMax`** (§4.1) : deux champs plutôt qu'un
   * champ et un drapeau, parce que la plage est le cas général et le nombre
   * fixe son cas particulier. L'inverse aurait imposé un `if` à chaque lecture.
   */
  repMin: number | null;
  repMax: number | null;
  /**
   * Durée d'une série, en secondes — `null` sur une ligne **en répétitions**.
   *
   * 🔴 **C'est ce champ qui décide de la nature de la ligne, pas un drapeau** :
   * `durationSeconds !== null` **est** « exercice en durée ». Un gainage, une
   * suspension à la barre, un *hollow hold* ne se comptent pas en répétitions,
   * et leur écrire « 1 rep » serait exactement ce que le §4.3 reproche à
   * `rounds` sur un superset — une valeur qu'on a promis d'ignorer, et que
   * quelqu'un finira par lire.
   *
   * ⚠️ **Exactement l'un des deux**, jamais les deux ni aucun : une ligne sans
   * reps ni durée ne dit pas quoi faire, une ligne avec les deux dit deux
   * choses. Refus nommé `measure_required`.
   */
  durationSeconds: number | null;
  targetWeightKg: number | null;
  targetRpe: number | null;
  setKind: SetKind;
  note: string | null;
  /**
   * ⚠️ **Le repos qui compte est celui du BLOC** (§4.3). Ce champ est au schéma
   * du §4.1 et le serveur le **force à `null` dès que le bloc n'est pas
   * `single`** : le §4.3 interdit tout repos entre les lignes d'un superset,
   * donc en stocker un serait stocker ce que la séance ignorera.
   */
  restSeconds: number | null;
  progressionKind: ProgressionKind;
  /** Forcé à `null` quand `progressionKind` vaut `none` — cf. `restSeconds`. */
  progressionIncrementKg: number | null;
}

/** Un bloc et ses lignes. */
export interface RoutineBlockDTO {
  clientId: string;
  id: number;
  kind: RoutineBlockKind;
  /** Rang dans la routine, renuméroté de 0 à n−1 par le serveur. */
  position: number;
  /** Nombre de tours d'un circuit. `null` pour `single` et `superset` (§4.3). */
  rounds: number | null;
  /** 🔴 Le repos du bloc — la base du chronomètre de l'étape 6 (§4.3). */
  restSeconds: number | null;
  lines: RoutineLineDTO[];
}

/**
 * Une routine complète.
 *
 * ⚠️ **`GET /api/routines` renvoie l'arbre entier de chaque routine**, là où
 * `GET /api/exercises` laisse les instructions au détail. Ce n'est pas une
 * inconséquence, c'est la même décision prise sur des volumes différents : les
 * instructions pèsent 571 Ko, une routine de huit lignes pèse ≈ 1,5 Ko. Trente
 * routines tiennent donc sous 50 Ko — moins que la base personnelle d'aliments
 * (§3.1, « < 100 Ko »). Les répliquer entières coûte moins qu'un aller-retour,
 * et rend **toutes** les routines consultables sans réseau : ce dont la salle a
 * précisément besoin.
 */
export interface RoutineDTO {
  /** 🔴 L'adresse de la routine (V2 §2.3-b). */
  clientId: string;
  id: number;
  name: string;
  notes: string | null;
  /**
   * L'échauffement à faire avant cette routine — une étape par ligne.
   *
   * 🔴 **Distinct de `notes`, et c'est une décision.** Un échauffement se lit
   * **avant** de commencer et se suit dans l'ordre ; une note se lit quand on
   * en a besoin. Les fondre obligerait l'écran à deviner laquelle des deux il
   * montre au moment de démarrer — c'est-à-dire à réinterpréter un champ.
   */
  warmup: string | null;
  isFavorite: boolean;
  blocks: RoutineBlockDTO[];
  createdAt: number;
  updatedAt: number;
}

/**
 * Corps de `PUT /api/routines/:clientId` — création **ou** remplacement.
 *
 * 🔴 **L'arbre entier voyage en une seule opération, et c'est le fond du
 * §2.3-b.** Le client possède déjà l'identité de la routine, de chacun de ses
 * blocs, de chacune de ses lignes, et celle des exercices visés : composer une
 * routine hors ligne ne demande donc **aucun place-titre**, aucune dépendance
 * entre opérations et aucun ordre de drainage à respecter. C'est exactement la
 * propriété que le §2.3-b achète, et la première fonction à l'encaisser.
 *
 * ⚠️ **Aucune position n'est transmise** : le rang est celui du tableau. C'est
 * ce qui rend le réordonnancement trivial — on déplace un élément, on
 * réenregistre — et ce qui évite de valider une numérotation reçue (trous,
 * doublons, valeurs négatives) qui n'apporterait rien.
 *
 * ⚠️ **Remplacement en bloc**, comme les repas types d'un modèle, les portions
 * d'un aliment et les ingrédients d'une recette : le client envoie l'état
 * voulu, jamais un diff.
 */
export interface RoutineInput {
  name: string;
  notes?: string | null;
  /** Une étape par ligne, comme `recipe.steps` (étape 7). */
  warmup?: string | null;
  isFavorite?: boolean;
  blocks: RoutineBlockInput[];
}

export interface RoutineBlockInput {
  clientId: string;
  kind: RoutineBlockKind;
  /** **Exigé** pour un circuit, forcé à `null` ailleurs (§4.3). */
  rounds?: number | null;
  restSeconds?: number | null;
  /** ⚠️ Un bloc sans ligne est **refusé 400 `block_empty`** — cf. `routes/routines.ts`. */
  lines: RoutineLineInput[];
}

export interface RoutineLineInput {
  clientId: string;
  exerciseClientId: string;
  sets: number;
  /** Avec `repMax`, **ou** `durationSeconds` — jamais les deux, jamais aucun. */
  repMin?: number | null;
  repMax?: number | null;
  /** Une ligne en durée : gainage, suspension, hollow hold. Cf. `RoutineLineDTO`. */
  durationSeconds?: number | null;
  targetWeightKg?: number | null;
  targetRpe?: number | null;
  setKind?: SetKind;
  note?: string | null;
  restSeconds?: number | null;
  progressionKind?: ProgressionKind;
  /** **Exigé** quand `progressionKind` vaut `double`, forcé à `null` sinon. */
  progressionIncrementKg?: number | null;
}

/**
 * Corps de `PATCH /api/routines/:clientId` — modification partielle.
 *
 * ⚠️ **Distinct du `PUT`, et les deux servent**, exactement comme pour les
 * exercices : basculer l'étoile depuis la liste n'a pas à réenvoyer l'arbre
 * entier. Il ne touche donc **jamais** aux blocs.
 */
export interface UpdateRoutineBody {
  name?: string;
  notes?: string | null;
  warmup?: string | null;
  isFavorite?: boolean;
}

/* --------------------------------------------------------------------------
 * Séance en direct (V2, étape 6) — `ARCHITECTURE_V2.md` §4.1, §4.2 et §4.5,
 * specs §12.3
 * ----------------------------------------------------------------------- */

/**
 * État d'une série dans une séance (§4.1).
 *
 * ⚠️ **`skipped` plutôt qu'une suppression** (§4.5) : retirer une série d'une
 * séance en cours ne l'efface pas — on veut savoir qu'elle était prévue. Une
 * série ne disparaît que si l'utilisateur la supprime pour de bon.
 *
 * ⚠️ **`pending` est un état stocké, pas une absence.** Une séance ouverte
 * matérialise son plan : toutes ses séries existent dès le départ, en attente.
 * C'est ce qui permet de rouvrir l'application au milieu d'une séance et de
 * retrouver ce qu'il reste à faire, hors ligne comme en ligne.
 */
export type SetStatus = 'pending' | 'done' | 'skipped';

/**
 * Une série réalisée — **l'unité de l'historique de musculation** (§4.1).
 *
 * 🔴 **`exerciseName` est COPIÉ, jamais joint — l'inverse exact de
 * `RoutineLineDTO.exerciseName`.** C'est la distinction référentiel /
 * historique du §4.2, et les deux cohabitent volontairement : renommer un
 * exercice **change** ce qu'une routine affiche (c'est un référentiel) et **ne
 * change rien** à une séance passée (c'est de l'historique). Exactement
 * `recipe_ingredient` contre `log_entry` en V1.
 *
 * 🔴 **Les cibles sont copiées ICI, sur la série, pas sur la séance** (§4.2).
 * C'est ce qui permet de relire « j'avais visé 4×8 à 60 kg, j'ai fait 8, 8, 7,
 * 6 » des mois plus tard, alors que la routine a évolué entre-temps. Sans cette
 * copie, la progression deviendrait illisible rétrospectivement — ce que le
 * §7.1 des specs veut précisément empêcher.
 *
 * ⚠️ **Répétitions OU durée, des deux côtés.** `routine_line` distingue les
 * deux depuis la migration `0010` ; une série qui l'exécute doit pouvoir dire
 * la même chose, sinon l'écran devrait convertir — et convertir, c'est
 * réinterpréter le modèle. Le réalisé (`reps` / `durationSeconds`) suit donc la
 * cible (`targetRepsMin`/`targetRepsMax` / `targetDurationSeconds`).
 */
export interface ActualSetDTO {
  /** 🔴 L'adresse de la série (§2.3-b) : `PUT /api/sessions/:c/sets/:sc`. */
  clientId: string;
  /** Informatif : clé primaire et pivot des jointures, il n'adresse rien. */
  id: number;
  /** Rang dans la séance — **donné par le client**, cf. `ActualSetInput`. */
  position: number;
  /**
   * Adresse de l'exercice, ou `null` s'il a été supprimé depuis.
   *
   * ⚠️ **Nullable, contrairement à `RoutineLineDTO.exerciseClientId`** : la clé
   * est en `SET NULL` (§4.2). C'est `exerciseName` qui porte l'information, et
   * il ne peut pas être perdu.
   */
  exerciseClientId: string | null;
  /** 🔴 **Copié** à l'écriture. Cf. l'en-tête : ce n'est **pas** une jointure. */
  exerciseName: string;
  /** Vignette, quand l'exercice existe toujours. */
  exerciseMediaKey: string | null;
  exerciseImageCount: number;
  /**
   * Regroupe les séries d'un même bloc à l'affichage — copie du `clientId` du
   * bloc de routine, ou une identité neuve pour un exercice ajouté en cours de
   * séance (§4.5).
   *
   * ⚠️ **Une chaîne libre, sans clé étrangère** : une FK vers `routine_block`
   * serait un `RESTRICT` déguisé sur l'historique, puisque le `PUT` d'une
   * routine remplace ses blocs en bloc.
   */
  blockKey: string | null;
  /** Rang de la série **sur sa ligne** : « série 3 sur 4 ». */
  setIndex: number;
  setKind: SetKind;

  /* --- Ce qui a réellement été fait ------------------------------------ */
  /** `null` tant que la série n'est pas faite — cf. `SetStatus`. */
  reps: number | null;
  /** Durée tenue, en secondes — la dualité de `RoutineLineDTO`. */
  durationSeconds: number | null;
  weightKg: number | null;
  rpe: number | null;
  status: SetStatus;

  /* --- Ce qui était visé, copié du plan (§4.2) -------------------------- */
  targetRepsMin: number | null;
  targetRepsMax: number | null;
  targetDurationSeconds: number | null;
  targetWeightKg: number | null;
  targetRpe: number | null;
}

/**
 * Une note libre, par exercice et par séance (specs §12.3).
 *
 * ⚠️ **Distincte de `WorkoutSessionDTO.notes`**, qui porte sur la séance
 * entière. Celle-ci répond à « pourquoi ce jour-là le développé couché a été
 * dur », et c'est ce qu'on relit en refaisant l'exercice.
 */
export interface SessionNoteDTO {
  exerciseClientId: string;
  note: string;
}

/**
 * Une séance, avec ses séries et ses notes.
 *
 * ⚠️ **L'arbre entier voyage**, comme celui d'une routine et pour la même
 * raison de volume : une séance de trente séries pèse ≈ 3 Ko. Et c'est en salle
 * qu'on la lit, c'est-à-dire là où il n'y a pas de réseau — une séance qu'il
 * faudrait « avoir ouverte une fois en ligne » ne tiendrait pas la promesse du
 * §3.1.
 *
 * ⚠️ **`finishedAt === null` signifie « en cours ».** Pas de champ `status` :
 * la forme se lit dans les données, comme `durationSeconds !== null` dit
 * « exercice en durée ». Un drapeau à côté serait une seconde vérité à tenir
 * d'accord avec la première.
 *
 * ⚠️ **`routineName` est copié et `routineClientId` peut être `null`** : la
 * séance survit à la suppression de sa routine (§4.2, `SET NULL`).
 */
export interface WorkoutSessionDTO {
  /** 🔴 L'adresse de la séance (§2.3-b). */
  clientId: string;
  id: number;
  /** `YYYY-MM-DD` dans le **fuseau réglé du serveur**, pas celui de l'appareil. */
  date: string;
  routineClientId: string | null;
  /** 🔴 **Copié** — cf. l'en-tête. */
  routineName: string | null;
  /** 🔴 Horodatage **absolu**, jamais un compteur (§4.5). Cf. `SessionInput`. */
  startedAt: number;
  /** `null` = séance en cours. */
  finishedAt: number | null;
  notes: string | null;
  sets: ActualSetDTO[];
  notesByExercise: SessionNoteDTO[];
  createdAt: number;
  updatedAt: number;
}

/**
 * Corps de `PUT /api/sessions/:clientId` — création **ou** remplacement.
 *
 * 🔴 **L'en-tête SEULEMENT : les séries ne sont pas ici, et c'est le cœur du
 * découpage de cette étape.** Une routine voyage d'un bloc parce qu'elle
 * s'édite comme un formulaire ; une séance se compose **au fur et à mesure
 * qu'elle a lieu** (§2.3-b, `PUT /api/sessions/:c/sets/:sc`). Si le `PUT` de la
 * séance portait ses séries, son **rejeu** — que la file peut déclencher des
 * heures plus tard — réécrirait le plan initial par-dessus des séries déjà
 * faites, et effacerait la séance en la « réparant ». Séparer les deux rend
 * ce défaut impossible plutôt qu'improbable.
 *
 * 🔴 **`startedAt` est fourni par le CLIENT, et c'est délibéré.** Une séance
 * commencée en mode avion à 18 h et drainée à 21 h doit afficher 18 h : prendre
 * l'heure du serveur daterait la séance de son *drainage*. Même raisonnement
 * que l'horodatage d'émission du §3.4, qui voyage dans le corps pour survivre à
 * IndexedDB et au rejeu.
 *
 * ⚠️ **`date` aussi est fournie**, pour la même raison, et **validée** : une
 * date fantaisiste est refusée 400, jamais remplacée en silence (la discipline
 * du `today=YYYY-MM-DD` des routes de poids, étape 11).
 */
export interface SessionInput {
  date: string;
  /** Adresse de la routine suivie, ou `null` pour une séance libre. */
  routineClientId?: string | null;
  /**
   * Nom de la routine au moment où la séance a commencé.
   *
   * ⚠️ **Le client le propose, le serveur le vérifie** : quand
   * `routineClientId` désigne une routine existante, c'est **son** nom qui est
   * copié, pas celui envoyé. Le champ ne sert donc qu'aux séances libres et aux
   * séances dont la routine a disparu entre-temps — l'écrire sans contrôle
   * laisserait un client mal réveillé baptiser une séance d'un nom qui n'a
   * jamais existé.
   */
  routineName?: string | null;
  startedAt: number;
  /** Passer une valeur **clôt** la séance ; `null` la rouvre. */
  finishedAt?: number | null;
  notes?: string | null;
}

/**
 * Corps de `PUT /api/sessions/:clientId/sets/:setClientId` — une série entière.
 *
 * 🔴 **Il n'existe AUCUN `PATCH` sur une série, et c'est une décision.** Le
 * `PATCH` existe ailleurs (exercices, routines, aliments) parce que la liste
 * qui l'appelle ne transporte pas la ressource entière : basculer une étoile
 * n'a pas à réenvoyer 571 Ko d'instructions. Ici c'est l'inverse — l'écran de
 * séance **tient la série complète sous les yeux**, elle fait une dizaine de
 * champs, et un `PUT` la réécrit en entier.
 *
 * Ce que ce refus achète, et c'est ce qui compte : un `PATCH` sur une série que
 * son `PUT` n'a pas encore drainée répondrait **404**, donc `failed` définitif
 * (§3.3) — une série perdue en silence, au milieu d'une séance, très exactement
 * le défaut que le hors ligne existe pour empêcher. Avec des `PUT` seuls,
 * l'ordre de drainage cesse d'être une condition de correction : chaque
 * opération écrit une ressource nommée, complète, et la dernière gagne.
 *
 * ⚠️ **Exactement une mesure de CIBLE** — plage de répétitions **ou** durée,
 * jamais les deux, jamais aucune. C'est `measure_required` de
 * `routes/routines.ts`, transposé. Le **réalisé** n'est soumis à rien : une
 * série `pending` n'a ni reps, ni durée, ni charge, et c'est son état normal.
 */
export interface ActualSetInput {
  /**
   * Adresse de l'exercice. ⚠️ **Exigée à l'écriture**, alors que le DTO la rend
   * nullable : on n'écrit jamais une série sans savoir de quel exercice elle
   * parle — c'est seulement *plus tard*, si l'exercice est supprimé, que la
   * clé devient `null` et que `exerciseName` reste seul à porter l'information.
   */
  exerciseClientId: string;
  /**
   * Nom de l'exercice, **proposé par le client et écrasé par le serveur** quand
   * l'exercice existe encore.
   *
   * 🔴 **C'est un repli, et il évite de perdre une série.** Le cas est étroit
   * mais réel : l'exercice supprimé sur l'ordinateur pendant que le téléphone
   * porte encore, dans sa file, les séries qui le citent. Sans ce champ, le
   * serveur n'aurait aucun nom à copier, la requête serait refusée 400 — donc
   * `failed` définitif (§3.3) — et **une série disparaîtrait d'une séance**,
   * très exactement ce que l'étape 6 existe pour empêcher.
   *
   * ⚠️ C'est la discipline de la ré-édition d'un bloc recette (étape 7) :
   * « `food` d'abord, valeurs figées en repli ». Le référentiel gagne quand il
   * est là ; la copie prend le relais quand il ne l'est plus.
   */
  exerciseName: string;
  blockKey?: string | null;
  /**
   * Rang dans la séance.
   *
   * ⚠️ **Il voyage, alors qu'aucune `position` de routine ne voyage** — et la
   * différence tient au geste, pas au goût. Une routine s'écrit d'un bloc :
   * son tableau *est* l'ordre, que le serveur renumérote de 0 à n−1. Une
   * séance se compose série après série : il n'y a pas de tableau à
   * renuméroter, donc le rang ne peut venir que du client, seul à savoir où il
   * insère.
   */
  position: number;
  setIndex: number;
  setKind?: SetKind;

  reps?: number | null;
  durationSeconds?: number | null;
  weightKg?: number | null;
  rpe?: number | null;
  status?: SetStatus;

  targetRepsMin?: number | null;
  targetRepsMax?: number | null;
  targetDurationSeconds?: number | null;
  targetWeightKg?: number | null;
  targetRpe?: number | null;
}

/**
 * Corps de `PUT /api/sessions/:clientId/plan` — le plan, posé d'un bloc.
 *
 * 🔴 **Cette route INSÈRE ce qui manque et ne réécrit JAMAIS ce qui existe**,
 * ce qui la rend idempotente **par structure** plutôt que par arbitrage
 * d'horodatage : rejouée, elle n'a rien à insérer, donc elle ne peut pas
 * repasser une série déjà faite en `pending`.
 *
 * 🔴 **Pourquoi elle existe : trente allers-retours au démarrage.** Poser le
 * plan série par série faisait attendre **≈ 25 s** avant l'ouverture de l'écran
 * de séance (mesuré à l'usage le 29/08/2026). « Une opération par geste » vaut
 * pour les gestes de la séance ; ouvrir une séance en est **un seul**.
 */
export interface SessionPlanInput {
  sets: (ActualSetInput & { clientId: string })[];
}

/** Corps de `PUT /api/sessions/:clientId/notes/:exerciseClientId`. */
export interface SessionNoteInput {
  note: string;
}

/**
 * Le dernier passage sur un exercice — `GET /api/exercises/:clientId/last`.
 *
 * Le §12.3 des specs le range parmi les cinq choses que l'écran de séance
 * affiche **simultanément** : « l'historique du dernier passage sur l'exercice
 * en cours ». Il répond à la seule question qu'on se pose devant la barre :
 * *j'avais mis combien, la dernière fois ?*
 *
 * ⚠️ **Préchargé à l'ouverture de la séance**, avec les images (§1.1 et §4.5) :
 * c'est en salle qu'on en a besoin, c'est-à-dire là où il n'y a pas de réseau.
 *
 * ⚠️ **Les séries sont celles de la DERNIÈRE séance qui portait cet exercice**,
 * pas les *n* dernières séries toutes séances confondues. Mélanger deux séances
 * donnerait « 8, 8, 7, 10, 10 » sans dire où l'une finit — illisible, et faux
 * dès qu'on compare des charges.
 *
 * ⚠️ **`null` quand l'exercice n'a jamais été fait**, et l'écran le dit
 * (« premier passage ») plutôt que d'afficher un vide qu'on prendrait pour un
 * échec de chargement.
 */
export interface LastPassageDTO {
  sessionClientId: string;
  date: string;
  /** Les séries de cette séance sur cet exercice, dans l'ordre. */
  sets: ActualSetDTO[];
  /** La note laissée ce jour-là sur cet exercice, s'il y en avait une. */
  note: string | null;
}

/* --------------------------------------------------------------------------
 * Progression automatique (V2 étape 7) — §4.4, specs §12.2
 * ----------------------------------------------------------------------- */

/**
 * Pourquoi la charge ne monte pas.
 *
 * 🔴 **Un motif porté par la RÉPONSE, jamais un silence.** C'est la discipline
 * d'`offStatus: 'unavailable'` (étape 6) : « une information portée par la
 * réponse, jamais une erreur ». Une proposition absente sans motif est
 * indistinguable d'une règle qui n'a pas tourné — et la première question qu'on
 * se pose devant une charge qui ne bouge pas est *pourquoi*.
 *
 * ⚠️ **Ces huit valeurs sont des CONSTATS, pas des reproches.** Aucune n'est
 * affichée comme un jugement : l'écran ne montre la ligne de progression que
 * lorsqu'elle **monte** (cf. `SessionBlockCard`). Le motif sert à la lecture de
 * l'API et au diagnostic, pas à commenter la séance.
 */
export type ProgressionHold =
  /** Jamais fait : on repart de la charge de départ de la routine (§4.4). */
  | 'no_passage'
  /** Aucune série de **travail** faite au dernier passage. */
  | 'no_work'
  /**
   * 🔴 **Exercice en DURÉE — explicitement hors du calcul** *(31/08/2026)*.
   *
   * La double progression se déclenche sur « le haut de la **plage** » ; or
   * `routine_line` ne porte qu'une durée **unique**, jamais une plage
   * (migration `0010`). Il n'y a donc pas de haut à atteindre : tenir la durée
   * prescrite est le cas **normal**, pas un signal. Une variante « durée tenue
   * ⇒ on monte » ferait monter la charge à **chaque** séance conforme —
   * c'est-à-dire une progression *linéaire*, qui est une **seconde règle**, et
   * le §4.4 en refuse une seconde tant qu'elle n'est pas une seconde valeur de
   * `progression_kind`.
   *
   * ⚠️ **Testé, jamais supposé** : le calcul ne présume nulle part que `reps`
   * existe. Le jour où une plage de durée existera, ce motif est l'endroit où
   * la règle s'ouvrira.
   */
  | 'duration'
  /** Aucune charge relevée — poids du corps : rien à incrémenter. */
  | 'no_load'
  /** Les séries de travail n'ont pas toutes la même charge (montée en pyramide). */
  | 'mixed_load'
  /** Une série de travail n'a pas atteint le haut de sa plage. */
  | 'range'
  /** Le RPE cible a été dépassé sur au moins une série. */
  | 'rpe'
  /** Le RPE était prescrit et n'a **pas** été relevé — on ne devine pas. */
  | 'rpe_missing';

/**
 * Ce que la double progression propose — **et rien de plus** (§4.4).
 *
 * 🔴 **Calculée à la LECTURE, jamais stockée.** C'est le même choix qu'à
 * `weightTrend.ts` (§4.6) et qu'aux macros de recette (specs §6.5), et il
 * achète la même propriété : corriger a posteriori une série de la séance
 * précédente **change immédiatement** la proposition suivante, sans
 * invalidation ni recalcul à déclencher, parce qu'aucune valeur périmée ne peut
 * exister.
 *
 * 🔴 **Elle propose, elle n'applique JAMAIS.** L'écran l'affiche, le champ
 * reste éditable, l'utilisateur accepte ou saisit autre chose. C'est la
 * discipline de l'écart kcal tenue depuis la V1 (specs §6.2) : « l'app décrit,
 * elle ne corrige pas, elle ne suggère pas d'office ».
 *
 * ⚠️ **`routine_line.target_weight_kg` n'est jamais réécrit** — il reste la
 * charge *de départ*, et cesse simplement d'être consulté dès qu'il existe un
 * passage précédent. C'est très exactement ce que dit le §4.4, et c'est
 * pourquoi `weightKg` le reprend sous `hold: 'no_passage'` : la règle **lit**
 * la valeur de départ, elle ne la touche pas.
 */
export interface ProgressionDTO {
  /**
   * La charge proposée pour les séries de **travail**, en kg.
   *
   * ⚠️ `null` quand il n'y a rien à proposer (poids du corps, exercice en
   * durée, charges mélangées). L'écran retombe alors sur ce qu'il faisait déjà
   * — la charge de la même série au dernier passage, puis la cible copiée.
   */
  weightKg: number | null;
  /**
   * La charge du dernier passage, point de départ de la décision.
   *
   * ⚠️ `null` quand elle n'existe pas ou n'est pas unique : c'est ce qui
   * distingue « on reste à 60 » de « on ne sait pas de quoi partir ».
   */
  previousWeightKg: number | null;
  /** L'incrément **prescrit par la routine**, en kg. */
  incrementKg: number;
  /** `true` seulement quand l'incrément a réellement été ajouté. */
  applied: boolean;
  /** Pourquoi on ne monte pas — `null` quand on monte. */
  hold: ProgressionHold | null;
}

/**
 * Réponse de `GET /api/exercises/:clientId/last` — le rappel d'un exercice.
 *
 * 🔴 **Deux champs, parce qu'il y a deux questions, et qu'elles n'ont pas le
 * même sujet.** « Qu'ai-je fait la dernière fois ? » porte sur le **passé** ;
 * « que dois-je mettre aujourd'hui ? » porte sur le **prochain geste**. Les
 * fondre en ajoutant `progression` à `LastPassageDTO` aurait mis sur l'objet
 * *passage* une valeur qui n'en est pas une — et `GET .../history`, qui rend
 * une liste de `LastPassageDTO`, aurait porté un champ toujours `null`.
 * C'est exactement ce que le §4.3 reproche à `rounds` sur un superset : « une
 * valeur qu'on a promis d'ignorer, et que quelqu'un finira par lire ».
 *
 * 🔴 **Et c'est ce qui donne un foyer à la proposition quand il n'y a AUCUN
 * passage.** Un exercice jamais fait rend `passage: null` — mais la question
 * « je mets combien ? » a quand même une réponse : la charge de départ de la
 * routine (§4.4). Portée par `progression`, elle est vérifiable au niveau de
 * l'API, et non seulement par un `??` côté écran.
 *
 * ⚠️ **`progression` est `null` quand aucune routine n'est nommée** (une fiche
 * d'exercice ouverte depuis la bibliothèque) ou quand la ligne ne prescrit
 * aucune progression (`progressionKind: 'none'`). Ne rien prescrire n'est pas
 * un échec de la règle : c'est l'absence de règle.
 */
export interface ExerciseRecallDTO {
  /** Le dernier passage, ou `null` si l'exercice n'a jamais été fait. */
  passage: LastPassageDTO | null;
  /** La charge proposée, ou `null` si aucune progression n'est prescrite. */
  progression: ProgressionDTO | null;
}

/* --------------------------------------------------------------------------
 * PWA et notifications (étape 9) — specs §6.7 et §9.5, archi §4.7 et §6.4
 * ----------------------------------------------------------------------- */

/**
 * Réglage d'une des trois notifications (§4.7).
 *
 * 🔴 **`time` est une heure du serveur, pas de l'appareil.** C'est `TZ` (§9) qui
 * borne la journée du scheduler : au moment où `jobs/scheduler.ts` évalue
 * « aucune pesée aujourd'hui », il n'y a aucun navigateur pour lui fournir un
 * fuseau. C'est exactement pourquoi `GET /api/days/today` et `todayISO()` ont
 * été conservés côté serveur quand le front est passé au fuseau de l'appareil.
 */
export interface NotificationSettingDTO {
  type: NotificationType;
  enabled: boolean;
  /** `HH:MM` sur 24 h, dans le fuseau du serveur. */
  time: string;
  /**
   * `YYYY-MM-DD` du dernier envoi **effectif**, ou `null`.
   *
   * C'est la clé d'idempotence du scheduler (§6.4) : un tick rejoué la même
   * journée ne renvoie rien. Une condition non remplie ne l'écrit pas — la
   * colonne dit « dernier envoi », pas « dernière évaluation ».
   */
  lastSentDate: string | null;
}

/** Corps de `PUT /api/notifications/:type`. */
export interface NotificationSettingInput {
  enabled: boolean;
  /** `HH:MM` sur 24 h, fuseau du serveur. */
  time: string;
}

/**
 * Abonnement Web Push tel que le navigateur le produit
 * (`PushSubscription.toJSON()`), réduit à ce que le §4.7 stocke.
 */
export interface PushSubscriptionInput {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

/**
 * Réponse de `GET /api/notifications` et de toutes ses mutations.
 *
 * Même parti pris que `GET /api/weight` et `GET /api/quick-access` : un seul
 * appel peint tout le volet des Réglages.
 */
export interface NotificationsResponse {
  /**
   * Clé publique VAPID, à passer telle quelle à `pushManager.subscribe()`.
   *
   * `null` si le serveur n'est pas configuré pour le push — l'écran le dit
   * plutôt que de laisser l'abonnement échouer par une erreur de navigateur.
   */
  vapidPublicKey: string | null;
  settings: NotificationSettingDTO[];
  /** Nombre d'abonnements enregistrés — un par appareil ayant accepté. */
  deviceCount: number;
  /** Jour dans le fuseau des notifications — celui qui pilote le déclenchement. */
  serverToday: string;
  /** Heure dans le fuseau des notifications, `HH:MM`. Affichée pour lever toute ambiguïté. */
  serverTime: string;
  /**
   * Fuseau dans lequel les heures ci-dessus sont interprétées (`Europe/Paris`).
   *
   * 🔴 C'est **lui** qui décide, pas celui du téléphone : le scheduler tourne
   * sans navigateur. Réglable depuis l'étape 11 — auparavant figé sur la
   * variable d'environnement `TZ`, ce qui envoyait le bilan de 21 h 30 à
   * 6 h 30 du matin pour un utilisateur à neuf heures de décalage.
   */
  timeZone: string;
  /**
   * `false` quand le fuseau est simplement hérité de `TZ`, faute de réglage.
   *
   * Sert à l'écran pour distinguer « c'est mon choix » de « c'est le défaut de
   * la machine » — deux situations qui affichent le même nom de fuseau mais
   * n'appellent pas la même action.
   */
  timeZoneIsExplicit: boolean;
}

/** Corps de `PUT /api/notifications/timezone`. */
export interface TimeZoneInput {
  /**
   * Identifiant IANA (`Europe/Paris`, `Australia/Sydney`), ou `null` pour
   * revenir au fuseau de la machine.
   *
   * ⚠️ Aucune liste n'est transportée : `Intl` porte la base IANA des deux
   * côtés. Le client construit son menu avec `Intl.supportedValuesOf`, le
   * serveur valide en demandant à `Intl` s'il sait interpréter la valeur.
   */
  timeZone: string | null;
}

/**
 * Charge utile d'une notification poussée.
 *
 * ⚠️ **`web/public/sw.js` recopie ce contrat à la main.** Le service worker
 * n'est pas passé par Vite — c'est un fichier servi tel quel, hors du bundle —
 * il ne peut donc rien importer d'ici. Modifier cette interface impose de
 * relire `sw.js`.
 */
export interface PushPayload {
  /** `test` couvre l'envoi d'essai des Réglages, qui n'est pas une des trois. */
  kind: NotificationType | 'test';
  title: string;
  body: string;
  /** Chemin applicatif à ouvrir au `notificationclick` (jamais une URL absolue). */
  url: string;
  /** Regroupe les notifications d'un même type : la plus récente remplace. */
  tag: string;
}

/** Résultat d'un envoi — `removed` compte les abonnements expirés nettoyés. */
export interface PushSendResult {
  sent: number;
  /** Abonnements supprimés parce que l'endpoint a répondu 404/410 (§6.4). */
  removed: number;
  /** Échecs sans suppression — panne passagère du service de push. */
  failed: number;
}

/* ==========================================================================
 * V2 §5 — intervals.icu  (étape 8)
 * ======================================================================= */

/**
 * Activité importée, telle que l'écran la lit (`ARCHITECTURE_V2.md` §5.1).
 *
 * 🔴 **Tout est facultatif sauf l'identité et la date.** Une activité dont on ne
 * sait pas lire la fréquence cardiaque reste une activité qui a eu lieu :
 * l'écran affiche « — » sur ce qu'il n'a pas, plutôt que de faire disparaître le
 * reste. C'est l'inverse du refus d'Open Food Facts (§5.2 de la V1), et la
 * raison de l'écart est nette — là-bas les macros seront **figées dans le
 * journal** (§4.1), ici rien n'est figé nulle part : ces données ne sont lues
 * que pour être regardées (§5.3).
 */
export interface IntervalsActivityDTO {
  /** L'identifiant intervals.icu — clé naturelle et distante (§5.1). */
  id: string;
  /** `YYYY-MM-DD` dans le **fuseau réglé** (§5.3), jamais celui de la source. */
  date: string;
  /** `Ride`, `Run`, `WeightTraining`… — vocabulaire de la source, non traduit. */
  type: string | null;
  name: string | null;
  /** Début, epoch ms UTC. `null` si la source n'a donné aucun horodatage lisible. */
  startAt: number | null;
  durationSeconds: number | null;
  distanceM: number | null;
  elevationM: number | null;
  /**
   * 🔴 Dépense énergétique — **affichée à côté des kcal consommées, jamais
   * soustraite** (§5.3, specs §7.3).
   */
  calories: number | null;
  averageHr: number | null;
  maxHr: number | null;
  /** Charge d'entraînement, telle qu'intervals.icu la calcule. */
  load: number | null;
}

/**
 * Données de santé d'une journée (§5.1).
 *
 * ⚠️ Aucune de ces valeurs n'est recalculée côté serveur — pas même `form`,
 * qu'on aurait pu déduire de `ctl − atl`. Le mot « forme » recouvre plusieurs
 * définitions selon les outils : en inventer une produirait un nombre qui
 * ressemble à celui d'un autre écran sans être le même.
 */
export interface IntervalsWellnessDTO {
  /** `YYYY-MM-DD` — celui de la source : un bien-être n'a pas d'instant (§5.3). */
  date: string;
  restingHr: number | null;
  /** Variabilité de la fréquence cardiaque. */
  hrv: number | null;
  sleepSeconds: number | null;
  sleepScore: number | null;
  /**
   * 🔴 **Poids importé — INDICATIF, jamais fusionné avec `weight_entry`**
   * (§5.3). Ce n'est pas la même mesure que la pesée « du matin, à jeun »
   * (specs §6.6) ; l'écrire dans le journal de poids polluerait la moyenne
   * lissée et **ferait taire le rappel de pesée**. L'écran le dit à chaque fois
   * qu'il l'affiche.
   */
  weightKg: number | null;
  steps: number | null;
  /** Charge chronique (fitness). */
  ctl: number | null;
  /** Charge aiguë (fatigue). */
  atl: number | null;
  form: number | null;
}

/**
 * Une journée de la fenêtre : ce qui a été fait, et ce qui a été mangé.
 *
 * 🔴 **`expendedKcal` et `consumedKcal` sont deux champs SÉPARÉS, et il n'en
 * existe volontairement pas de troisième.** C'est le §5.3 rendu dans le
 * contrat d'API plutôt que dans une consigne : « les calories dépensées sont
 * affichées **à côté** des calories consommées, jamais soustraites. Aucun
 * “restant ajusté” n'existe. » Un champ `netKcal` ou `adjustedRemaining` serait
 * le couplage automatique que le §7.3 des specs interdit — et une fois exposé
 * dans un DTO, il finirait par être affiché.
 *
 * ⚠️ Le calcul du restant, lui, vit dans `services/macros.ts` et ne lit **pas**
 * ces tables. La séparation est vérifiable mécaniquement, et le banc de l'étape
 * la vérifie.
 */
export interface IntervalsDayDTO {
  date: string;
  activities: IntervalsActivityDTO[];
  wellness: IntervalsWellnessDTO | null;
  /**
   * Somme des calories des activités du jour. `null` si aucune activité n'en
   * porte — et non `0`, qui affirmerait qu'on n'a rien dépensé.
   */
  expendedKcal: number | null;
  /**
   * kcal **consommées** ce jour-là, lues au journal. `null` si la journée n'a
   * jamais été ouverte — là encore, `0` dirait « rien mangé » là où la vérité
   * est « rien saisi ».
   *
   * ⚠️ Lecture **seule**, et dans ce sens **uniquement** : le journal ne sait
   * rien d'intervals.icu, c'est l'écran d'activité qui va chercher le journal.
   * L'inverse serait le couplage interdit.
   */
  consumedKcal: number | null;
}

/**
 * État de la synchronisation — ce que les Réglages affichent (§5.2).
 *
 * ⚠️ **La clé n'y figure pas, et ne doit jamais y figurer.** Elle est un secret
 * serveur, posé par `infra/set-intervals-env.sh` ; il n'existe aucun champ de
 * saisie dans l'interface et aucune route ne la renvoie, pas même tronquée.
 */
export interface IntervalsStatusDTO {
  /** `false` = aucune clé posée, d'où qu'elle vienne. Le tick tourne à vide. */
  configured: boolean;
  /**
   * D'où vient la clé en vigueur — `null` s'il n'y en a aucune.
   *
   * 🔴 **L'écran doit le DIRE.** Une clé posée depuis les Réglages l'emporte sur
   * `INTERVALS_API_KEY` ; sans cette information, supprimer la première sans
   * désactiver la synchronisation serait incompréhensible — on chercherait
   * longtemps pourquoi « aucune clé » continue d'importer des activités.
   *
   * ⚠️ **La clé elle-même ne voyage jamais**, pas même tronquée. On ne reconnaît
   * pas une clé d'API à ses quatre derniers caractères : ce serait une fuite
   * pour un confort nul.
   */
  keySource: 'setting' | 'env' | null;
  /** Athlète interrogé — `0` désigne l'athlète authentifié. */
  athleteId: string;
  /** Fin de la dernière tentative, epoch ms. `null` = aucune n'a eu lieu. */
  lastSyncAt: number | null;
  /** `null` tant qu'aucune tentative n'a eu lieu. */
  lastSyncOk: boolean | null;
  /** Motif du dernier échec, tronqué. `null` si la dernière tentative a abouti. */
  lastError: string | null;
  /** Ce que la dernière tentative a écrit. */
  lastActivityCount: number;
  lastWellnessCount: number;
  /** Largeur de la fenêtre glissante, en jours (§5.2). */
  windowDays: number;
}

/**
 * Réponse de `GET /api/intervals` — **tout l'écran en un seul appel**.
 *
 * Même parti pris que `GET /api/weight`, `GET /api/quick-access` et
 * `GET /api/notifications` : l'appelant ne demande pas « des activités », il
 * demande de quoi peindre un écran. Et c'est ce qui permet aux Réglages de
 * partager **la même entrée de cache** que Progression pour afficher un statut,
 * plutôt que d'ouvrir un second aller-retour pour deux lignes de texte.
 */
export interface IntervalsResponse {
  status: IntervalsStatusDTO;
  /** Jour du serveur, dans le fuseau réglé. */
  today: string;
  /** La fenêtre, du plus récent au plus ancien. Un jour sans rien est omis. */
  days: IntervalsDayDTO[];
  /**
   * Verdict de l'ingestion que **cet appel** vient de déclencher, s'il en a
   * déclenché une (`POST /api/intervals/sync`). Absent en lecture.
   *
   * ⚠️ Distinct de `status.lastSyncOk`, qui décrit la **dernière** ingestion
   * quelle qu'elle soit — y compris un tick de fond d'il y a vingt minutes.
   * Celui-ci répond à « qu'a donné le bouton que je viens de presser », qui est
   * une autre question : sans lui, presser « Synchroniser » alors que rien n'a
   * changé ne produirait aucun retour visible.
   */
  lastRun?: IntervalsRunDTO;
}

/** Ce qu'une ingestion déclenchée à la main vient de faire. */
export interface IntervalsRunDTO {
  /** `skipped` = aucune clé, donc rien n'a été tenté. */
  status: 'skipped' | 'ok' | 'error';
  activities: number;
  wellness: number;
  /** Motif d'échec, tronqué. Absent si l'ingestion a abouti. */
  error?: string;
}

/**
 * Corps de `PUT /api/intervals/key`.
 *
 * 🔴 **Revirement assumé** *(05/09/2026, demandé après usage)*. Le cahier des
 * charges de l'étape disait « jamais un champ de saisie de clé dans
 * l'interface » ; poser la clé demandait alors un `scp`, un `ssh -t`, un mot de
 * passe sudo et un script. Acceptable pour un geste qu'on fait deux fois ; plus
 * du tout à la première clé refusée.
 *
 * ⚠️ **Ce que le stockage en base coûte**, et il faut le savoir plutôt que le
 * découvrir : la clé se retrouve **en clair dans chaque sauvegarde** (§8.2).
 * C'est l'inverse de `session.id`, qui ne stocke qu'un SHA-256 pour cette raison
 * exacte — mais une clé d'API doit être **réémise** à chaque requête, donc elle
 * ne peut pas être hachée. Les sauvegardes vivent en `750 nutri:nutri` sur le
 * même VPS, au même niveau de protection que la base.
 *
 * ⚠️ **Hors file de synchronisation** (`shared/syncable.ts`) : une clé mise en
 * attente dormirait en clair dans IndexedDB, côté navigateur, jusqu'au retour du
 * réseau. Un secret ne se met pas en file.
 */
export interface IntervalsKeyInput {
  /** La clé, ou `null` pour retirer celle posée depuis l'application. */
  apiKey: string | null;
}

/* --------------------------------------------------------------------------
 * Tableau de bord statistiques (V2 étape 9) — §6, specs §12.5
 * ----------------------------------------------------------------------- */

/**
 * 🔴 **Rien de ce qui suit n'est stocké nulle part** (§6.1). Tout sort de
 * fonctions pures de `server/src/services/stats.ts`, sur le modèle de
 * `weightTrend.ts` : une table `daily_nutrition_totals` a été envisagée puis
 * **écartée**, parce que le §4.1 rend la correction du journal *courante* — la
 * table serait donc fausse souvent, pas exceptionnellement, et fausse **en
 * silence**.
 *
 * 🔴 **Et aucun de ces DTO ne porte de droite de régression, de coefficient de
 * corrélation ni de conseil** (§6.2, règle générale). Il n'y a volontairement
 * aucun champ pour en accueillir un : l'application mesure, l'utilisateur
 * conclut. C'est l'écart kcal de la V1 (« ne corrige jamais, ne bloque jamais,
 * ne suggère jamais », specs §6.2) appliqué à tout le tableau de bord.
 */

/**
 * Les quatre plages du tableau de bord.
 *
 * 🔴 **Alias de `WeightRange`, et non un second type aux mêmes valeurs.** Les
 * deux vivent sur le **même onglet** : deux vocabulaires de plages y auraient
 * produit « 90 j » ici et « 3 mois » là, et surtout deux listes à tenir
 * synchronisées à la main. L'alias rend la divergence impossible tout en
 * nommant la chose à l'endroit où on la lit.
 */
export type StatsRange = WeightRange;

/* ---- Nutrition ---------------------------------------------------------- */

/**
 * Le respect d'une cible sur la plage, pour **une** macro.
 *
 * ⚠️ **`days` compte les jours qui portaient un objectif**, pas les jours de la
 * plage : une journée sans objectif n'est ni atteinte ni manquée, elle ne
 * compte pas. Ranger ces jours d'un côté ou de l'autre serait faux des deux
 * façons — c'est la discipline du `null` de `weeklyTrend()`.
 */
export interface MacroTargetRateDTO {
  /** Jours de la plage portant un objectif pour cette macro. */
  days: number;
  /** Parmi eux, ceux tombés dans la bande de ±5 % (`shared/macroTarget.ts`). */
  onTarget: number;
  /** `onTarget / days` en pourcentage entier. `null` si `days === 0`. */
  percent: number | null;
}

/** Un jour de la série nutritionnelle. */
export interface NutritionPointDTO {
  date: string;
  /** Consommé du jour — les quatre macros, telles que le Journal les somme. */
  consumed: MacroSet;
  /**
   * Moyennes glissantes des **kcal** sur 7 et 30 jours calendaires.
   *
   * 🔴 **Des kcal seulement, et c'est une mesure de poids de réponse.** Porter
   * les deux moyennes des quatre macros triplerait la série — ≈ 450 Ko sur la
   * plage « Tout », persistés en IndexedDB — pour trois courbes de plus que
   * l'écran ne trace pas. Les quatre macros ont bien leurs moyennes glissantes
   * (§6.2) : elles voyagent dans `means`, en **chiffres du jour** plutôt qu'en
   * séries, ce qui est la forme sous laquelle on les lit.
   *
   * ⚠️ Sept et trente **jours calendaires**, jamais « les 7 dernières journées
   * saisies » — même règle qu'au lissage du poids : sauter trois jours ne doit
   * pas faire remonter la fenêtre dix jours en arrière.
   *
   * 🔴 **La moyenne ne porte que sur les jours SAISIS de la fenêtre, et vaut
   * `null` s'il n'y en a aucun.** Compter une journée non saisie comme
   * 0 kcal ferait plonger la courbe pour un jour où l'on a mangé sans le
   * noter — c'est-à-dire mesurer la saisie en croyant mesurer l'apport. La
   * régularité, elle, se lit à part (`logged`, `daysLogged`), là où c'est
   * bien ce qu'on veut savoir. Un `null` laisse un **trou** dans la courbe,
   * ce qui est la lecture juste : on ne sait pas.
   */
  kcal7: number | null;
  kcal30: number | null;
  /**
   * La journée porte-t-elle au moins une entrée ? C'est la **régularité de
   * saisie** du §6.2, jour par jour.
   *
   * ⚠️ Distinct de « consommé nul » : une journée ouverte sans rien manger et
   * une journée jamais saisie donnent toutes deux 0 kcal, et ne disent pas la
   * même chose.
   */
  logged: boolean;
}

/**
 * Les moyennes glissantes du dernier jour de la plage — quatre macros.
 *
 * ⚠️ **`null` quand la fenêtre ne contient aucune journée saisie**, même règle
 * que `NutritionPointDTO.kcal7` : un zéro se lirait « je n'ai rien mangé »
 * là où la vérité est « je n'ai rien noté ». Et le nombre de jours réellement
 * moyennés voyage avec, sans quoi une moyenne sur une seule journée aurait
 * l'air aussi solide qu'une moyenne sur trente.
 */
export interface RollingMeansDTO {
  /** Moyenne sur les 7 derniers jours calendaires. */
  mean7: MacroSet | null;
  /** Journées saisies ayant servi à `mean7`. */
  days7: number;
  /** Moyenne sur les 30 derniers jours calendaires. */
  mean30: MacroSet | null;
  /** Journées saisies ayant servi à `mean30`. */
  days30: number;
}

/**
 * Apport moyen sur 14 jours **à côté** de la variation de poids des mêmes
 * 14 jours.
 *
 * 🔴 **C'est le remplacement explicite de la corrélation apport ↔ tendance de
 * poids, écartée au §6.2** — « sur un sujet unique, sans période de contrôle,
 * une corrélation entre deux séries temporelles toutes deux tendancielles est
 * fallacieuse presque par construction ; et elle serait lue comme causale ».
 * On montre donc **deux nombres côte à côte, sans coefficient**, et l'écran ne
 * dit pas ce qu'il faudrait en conclure.
 *
 * ⚠️ **Les mêmes 14 jours des deux côtés**, sans quoi la lecture côte à côte
 * mentirait sur ce qu'elle rapproche.
 */
export interface IntakeVsWeightDTO {
  /** Premier et dernier jour de la fenêtre commune. */
  from: string;
  to: string;
  /** Apport moyen sur la fenêtre, en kcal. `null` si aucune journée saisie. */
  meanKcal: number | null;
  /** Jours saisis ayant servi à cette moyenne. */
  loggedDays: number;
  /** Variation du poids **lissé** sur la fenêtre, en kg. `null` si trop court. */
  weightDeltaKg: number | null;
  /** Pesées disponibles dans la fenêtre. */
  weighIns: number;
}

/** Réponse de `GET /api/stats/nutrition`. */
export interface NutritionStatsResponse {
  range: StatsRange;
  /** Bornes effectives de la plage, incluses. */
  from: string;
  to: string;
  /** Jours de la plage portant une journée en base. */
  daysWithDay: number;
  /** Parmi eux, ceux portant au moins une entrée (régularité de saisie). */
  daysLogged: number;
  onTarget: {
    protein: MacroTargetRateDTO;
    carbs: MacroTargetRateDTO;
    fat: MacroTargetRateDTO;
    kcal: MacroTargetRateDTO;
  };
  means: RollingMeansDTO;
  points: NutritionPointDTO[];
  intakeVsWeight: IntakeVsWeightDTO;
}

/* ---- Musculation -------------------------------------------------------- */

/** Ce qu'un muscle a reçu sur une semaine. */
export interface MuscleWeekLoadDTO {
  /** Nom **français** du muscle (traduit à l'import, étape 4). */
  muscle: string;
  /**
   * Séries de travail pondérées : pleines en primaire, **à moitié** en
   * secondaire (`shared/muscles.ts`, la règle de la carte corporelle).
   */
  sets: number;
  /**
   * Tonnage (Σ reps × charge) attribué à ce muscle, en kg, même pondération.
   *
   * ⚠️ **À lire à muscle constant, dans le temps — jamais entre muscles**
   * (§6.2) : 100 kg de squat et 100 kg de curl ne sont pas la même chose.
   */
  tonnage: number;
}

/** Une semaine d'entraînement. */
export interface TrainingWeekDTO {
  /** Lundi de la semaine, `YYYY-MM-DD`. */
  weekStart: string;
  /** Séances distinctes portant au moins une série faite. */
  sessions: number;
  /** Séries de **travail** faites, tous exercices confondus. */
  workSets: number;
  /** Par muscle — seuls ceux réellement travaillés cette semaine-là. */
  byMuscle: MuscleWeekLoadDTO[];
}

/** Fréquence d'un muscle sur la plage entière. */
export interface MuscleFrequencyDTO {
  muscle: string;
  /** Séances distinctes ayant travaillé ce muscle. */
  sessions: number;
  /** Séries pondérées cumulées. */
  sets: number;
}

/** Le RPE moyen d'une séance — un point de la série temporelle. */
export interface SessionRpeDTO {
  sessionClientId: string;
  date: string;
  /** Moyenne des RPE **saisis** des séries de travail faites. */
  rpe: number;
  /** Séries ayant effectivement porté un RPE — le reste ne compte pas. */
  ratedSets: number;
}

/**
 * Record **estimé** par la formule d'Epley — jamais un record.
 *
 * 🔴 **`charge × (1 + reps ÷ 30)`, plafonné à 12 répétitions** (§6.2) :
 * au-delà la formule diverge et produit des nombres qui ne veulent rien dire.
 * Une série de 20 répétitions n'est donc pas ignorée par erreur, elle est
 * **hors du domaine de validité** de ce qu'on sait calculer — et le taire
 * aurait affiché un record de 150 kg à quelqu'un qui n'a jamais chargé 90.
 *
 * ⚠️ **Le mot « estimé » n'est pas décoratif** : l'écran l'écrit, parce qu'un
 * 1RM calculé et un 1RM soulevé ne sont pas la même chose, et que le second
 * n'est jamais mesuré ici.
 */
export interface EstimatedMaxDTO {
  exerciseClientId: string;
  exerciseName: string;
  /** Le 1RM estimé, en kg. */
  estimatedKg: number;
  /** La série qui l'a produit. */
  kg: number;
  reps: number;
  date: string;
}

/** Réponse de `GET /api/stats/training`. */
export interface TrainingStatsResponse {
  range: StatsRange;
  from: string;
  to: string;
  /** Séances portant au moins une série faite. */
  sessions: number;
  /** Séries de travail faites sur la plage. */
  workSets: number;
  weeks: TrainingWeekDTO[];
  frequency: MuscleFrequencyDTO[];
  rpe: SessionRpeDTO[];
  estimatedMaxes: EstimatedMaxDTO[];
  /**
   * Muscles rencontrés sur la plage, du plus travaillé au moins travaillé.
   *
   * ⚠️ Sert au sélecteur du tonnage, qui doit se lire **à muscle constant** :
   * sans cette liste, l'écran devrait la reconstituer depuis `weeks`, où un
   * muscle absent d'une semaine ne figure pas.
   */
  muscles: string[];
  /**
   * 🔴 **Séries dont le muscle est inconnu** — exercice supprimé du
   * référentiel, ou exercice `perso` dont les muscles sont saisis autrement.
   *
   * ⚠️ **Signalées, jamais tues**, exactement comme la carte corporelle le fait
   * sous elle : les taire laisserait croire que ces séances n'ont rien
   * travaillé, et inventer une correspondance approchée serait deviner.
   */
  unattributedSets: number;
}

/* ---- Santé / activité, et croisements ----------------------------------- */

/** Un jour de bien-être, tel qu'importé (§5.3 : lecture seule et indicatif). */
export interface HealthPointDTO {
  date: string;
  restingHr: number | null;
  hrv: number | null;
  sleepSeconds: number | null;
  sleepScore: number | null;
  steps: number | null;
  ctl: number | null;
  atl: number | null;
  form: number | null;
  /**
   * Dépense énergétique du jour, somme des activités.
   *
   * 🔴 **Elle n'est jamais soustraite de quoi que ce soit** (specs §7.3, §5.3).
   * Il n'existe aucun champ de « kcal nettes » ni de « restant ajusté », ici
   * pas plus qu'ailleurs — et c'est vérifié par `grep` au banc.
   */
  expendedKcal: number | null;
  /** Charge d'entraînement du jour, somme des activités. */
  load: number | null;
}

/**
 * Un point de nuage — **deux grandeurs et une date, rien d'autre**.
 *
 * 🔴 **Aucune droite, aucun coefficient** (§6.2). Le DTO n'a pas de champ pour
 * en porter un, ce qui rend l'oubli impossible plutôt qu'improbable : c'est la
 * discipline du §5.3, où l'absence de « restant ajusté » est garantie par
 * l'absence de champ.
 */
export interface ScatterPointDTO {
  date: string;
  x: number;
  y: number;
}

/** Réponse de `GET /api/stats/health`. */
export interface HealthStatsResponse {
  range: StatsRange;
  from: string;
  to: string;
  /** Vrai si une clé intervals.icu est en place — sinon l'écran le dit. */
  configured: boolean;
  points: HealthPointDTO[];
  /**
   * Croisement sommeil ↔ RPE moyen de la séance du même jour.
   * `x` = heures de sommeil, `y` = RPE moyen.
   */
  sleepVsRpe: ScatterPointDTO[];
  /**
   * Croisement charge d'entraînement ↔ tendance de poids.
   * `x` = charge hebdomadaire cumulée, `y` = tendance en kg/semaine à la fin
   * de cette semaine-là.
   */
  loadVsWeightTrend: ScatterPointDTO[];
}
